import { z } from 'zod';
import { LooseDate, Timestamp, UserRef, Uuid } from './common.js';
import { ColumnType, GroupTitle, ImportContainer, ProfileExclusion } from './import-profile.js';
import { ExtractorTier } from './org-settings.js';
import { NameConflictCandidate } from './subjects.js';
import { SEAT_PARTS } from './seating.js';
import {
  FACT_TYPE_FIELDS,
  FACT_TYPE_REQUIRED_FIELDS,
  FactType,
  isFactTypeField,
  isImportTargetableFactType,
  SubjectStatus,
} from './vocab.js';

/**
 * **Import: the plan a session proposes, and the session itself**
 * (`docs/notes/2026-09-06-import-design.md` §2.2 / §2.3).
 *
 * Every file the office uploads is one session: code parses the container and
 * builds a profile, the model asks about the shape of the import and proposes a
 * **typed plan**, the office edits it, and a deterministic executor commits it
 * (§1.1, §1.2, §1.7). This file is the plan and the session's wire shapes. It is
 * not the profile — chunk 1 owns `import-profile.ts` — and it is not the
 * endpoints, which are chunks 3 and 4.
 *
 * Two rules from §1.7 shape everything here:
 *
 *  - **The plan is a document a person reads and edits.** Every vocabulary is
 *    closed and every list is spelled in end-result words, because the Plan tab
 *    renders the targets as a table and the office ticks exceptions in it. A
 *    target nothing can execute must not parse.
 *  - **The executor is deterministic and the model is never in it.** So the plan
 *    carries no free-form instruction: a transform is one of six, a bin reason is
 *    one of nine, and everything the executor does is named here.
 */

/* ————————————————————————— the plan ————————————————————————— */

/**
 * The plan's own version, `1` today. It is on the document rather than implied by
 * the row's `version` column, which counts *edits within a session* — this one
 * says which contract the document was written against.
 */
export const IMPORT_PLAN_VERSION = 1;

/**
 * **A column of the file, addressed the way the profile addresses it** — the
 * 1-based `index` that is the truth, and the `header` the office reads.
 *
 * Both halves, and not just the header, because a header is not an identifier:
 * §2.2's `updates` sheet carries **two columns named `Content Type`**, monday
 * repeats the header once per group, and a sheet with no header row has columns
 * with no name at all. A plan keyed on the words alone would have silently mapped
 * one of a pair and left the other, or refused to say anything about either.
 *
 * So the index is what the executor reads and the header is what the Plan tab
 * prints — which is also what keeps the plan checkable by eye, the property that
 * argued for the bare string in the first place. `header` may be empty (an
 * unnamed column); `index` is 1-based, matching the profile's `ColumnProfile`
 * and the spreadsheet's own column numbers. A document has one pseudo-column at
 * index 1, whose header is the parser's name for it (`body`).
 *
 * The vocabulary of legal values is the **profile's**, which chunk 1 owns; this
 * contract only says a reference is an index and a header, so that a plan written
 * against one build's profiler still parses against the next.
 */
export const ColumnRef = z
  .object({
    /** 1-based, as `ColumnProfile.index` is. */
    index: z.number().int().positive(),
    /** What the file called it, for the Plan tab. Empty on an unnamed column. */
    header: z.string(),
  })
  .strict();
export type ColumnRef = z.infer<typeof ColumnRef>;

/**
 * **Column or group, told apart by a field rather than by a reserved word.**
 *
 * The **group** a row arrived in is not a column of the sheet: monday exports the
 * group's title alone in column A and repeats the header per group, so "which
 * group is this row in" is a fact the parser derives and no column carries — and
 * the board's whole status map (§2.2's worked example) is keyed on it.
 *
 * It was a reserved `ColumnRef` value once, on the argument that a rule reads the
 * same either way. It cannot be: a sheet with a real column named `group` would
 * have had every one of its rules quietly re-pointed at the parser's derived
 * value, with nothing anywhere to say so. So `by` says which of the two a rule
 * matches on, and only the `column` arm carries a column.
 */
export const RULE_MATCH_KINDS = ['column', 'group'] as const;

/**
 * **A discriminated arm's own kind, taken from the closed list that names it.**
 *
 * The four kind lists in this file — {@link RULE_MATCH_KINDS},
 * {@link RECORD_RULE_KINDS}, {@link NAME_RULE_KINDS}, {@link TRANSFORM_KINDS} —
 * are exported for the planner and the Plan tab, and a constant the schema does
 * not validate against is a vocabulary that drifts in silence: a kind added to
 * the union and not to the list, or the other way round, would be a plan the
 * office is offered and the contract refuses.
 *
 * This ties one direction where a `z.literal('row')` could not: an arm cannot
 * name a kind its list does not carry. The other direction — a list member with
 * no arm — is pinned in `import.spec.ts`, which asserts each union's own option
 * list *is* the constant.
 *
 * **`const K` is load-bearing and was missing until chunk 3 read the unions from
 * outside this package.** Without it TypeScript resolves `K` to its constraint
 * rather than to the literal that was passed, so every union built through this
 * helper inferred `kind: 'row' | 'heading'` on *both* arms — which parses and
 * validates exactly the same and stops narrowing entirely: `rule.kind === 'row'`
 * no longer reaches `rule.requires`, and the only way to read a plan would have
 * been the cast this helper's own docblock calls the bug it exists to close.
 * Nothing inside `shared/` narrowed on one, so its typecheck was green throughout.
 */
const kindOf = <L extends readonly string[], const K extends L[number]>(
  _kinds: L,
  kind: K,
): z.ZodLiteral<K> => z.literal(kind);

/**
 * **The half of a matching rule that does the matching**, written once and
 * spelled out three times.
 *
 * Three rules share it and differ only in their verb — a resolution level, a
 * subject status, a record field — but each is written out as its own two-arm
 * union rather than generated from a helper: zod's `discriminatedUnion` takes
 * plain object schemas, and a generic that builds them loses the discriminant's
 * literal type, so `rule.by === 'column'` would stop narrowing to the arm that
 * has a column. A reader who cannot narrow writes a cast, and a cast here is the
 * bug this union was introduced to close.
 *
 * The match is equality on the text, in both arms: a plan a person edits cannot
 * carry a regular expression.
 */
const columnMatch = {
  by: kindOf(RULE_MATCH_KINDS, 'column'),
  column: ColumnRef,
  equals: z.string(),
};
const groupMatch = { by: kindOf(RULE_MATCH_KINDS, 'group'), equals: z.string() };

/** What one record of the file is: a row of a grid, or a heading in a document. */
export const RECORD_RULE_KINDS = ['row', 'heading'] as const;

/**
 * `requires` is what makes a row a *person* rather than a summary line: the
 * board's nine group-summary rows carry aggregates in typed columns and no name,
 * so a rule requiring `Name` excludes them without a bespoke check.
 */
export const RecordRule = z.discriminatedUnion('kind', [
  z.object({ kind: kindOf(RECORD_RULE_KINDS, 'row'), requires: z.array(ColumnRef) }).strict(),
  z.object({ kind: kindOf(RECORD_RULE_KINDS, 'heading') }).strict(),
]);
export type RecordRule = z.infer<typeof RecordRule>;

/**
 * The deterministic exclusions, named so the office can see what the count it is
 * shown is made of: the CSV's three junk lines, the board's repeated headers and
 * summary rows, the sentinel `zzz Cold Outreach` (§1.17), the 28 date-only rows
 * of withdrawn LinkedIn profiles.
 */
export const EXCLUSION_RULES = [
  'leading_lines',
  'repeated_header',
  'single_cell_row',
  'nameless_row',
  'blank_row',
  'sentinel_name',
  'date_only_row',
] as const;
export const ExclusionRule = z.enum(EXCLUSION_RULES);
export type ExclusionRule = z.infer<typeof ExclusionRule>;

/**
 * One exclusion and what it took out. `detail` is required — a line the office
 * reads as "3 leading lines" or "the sentinel row zzz Cold Outreach", never a
 * bare rule name — and `count` is what the preview will be held to.
 */
export const PlanExclusion = z
  .object({
    rule: ExclusionRule,
    detail: z.string().min(1),
    count: z.number().int().nonnegative(),
  })
  .strict();
export type PlanExclusion = z.infer<typeof PlanExclusion>;

/**
 * **"There is no sheet", in both spellings a model reaches for.**
 *
 * `sheet` and `header_row` are absent on a CSV and on a document, and a document
 * session found out the hard way that *absent* was the only legal way to say so:
 * a PDF import spent all three attempts being refused, once for naming a sheet
 * the file has none of, once for `null`, and once for the first again — because
 * nothing in the refusal said the key could simply be left out. A key that may be
 * missing and a key that may be null are the same statement about the file, so
 * both parse, and `null` is folded to absent here so that every reader below —
 * the executor's sheet lookup, the Plan tab's `?? null` — is unchanged.
 */
const absentOrNull = <T extends z.ZodTypeAny>(inner: T) =>
  inner
    .nullable()
    .transform((value) => value ?? undefined)
    .optional();

export const ImportPlanSource = z
  .object({
    /** The workbook sheet this plan reads. Absent (or null) for a CSV or a document. */
    sheet: absentOrNull(z.string().min(1)),
    /** 1-based, and absent (or null) when the parser found the header itself. */
    header_row: absentOrNull(z.number().int().positive()),
    record_rule: RecordRule,
    exclusions: z.array(PlanExclusion),
  })
  .strict();
export type ImportPlanSource = z.infer<typeof ImportPlanSource>;

/**
 * Where a person's name comes from. `columns` covers the CSV's `First` + `Last`;
 * `heading` is the bios PDF, where the heading *is* the name and the record rule
 * says so.
 */
export const NAME_RULE_KINDS = ['column', 'columns', 'heading'] as const;

export const NameRule = z.discriminatedUnion('kind', [
  z.object({ kind: kindOf(NAME_RULE_KINDS, 'column'), column: ColumnRef }).strict(),
  z
    .object({
      kind: kindOf(NAME_RULE_KINDS, 'columns'),
      columns: z.array(ColumnRef).min(2),
      /** What goes between them. A space unless the plan says otherwise. */
      separator: z.string().default(' '),
    })
    .strict(),
  z.object({ kind: kindOf(NAME_RULE_KINDS, 'heading') }).strict(),
]);
export type NameRule = z.infer<typeof NameRule>;

/**
 * `couple_heading` is §1.16: a heading with a conjunction is two people and one
 * deterministic `family_relation(spouse)`, with the heading as evidence.
 *
 * `couple_cell` is the grid's word for what `couple_heading` is on a document
 * (§1.16a): a name cell the parser reads as two people becomes two records off
 * one row — locators `<sheet>:<row>#1` and `#2` — with the row's identifiers,
 * facts and prose kept on the first-named half and the same spouse fact written
 * from it. The file does not say which spouse the member number belongs to, and
 * the rule does not guess: it says where it put them and leaves the move to the
 * office.
 */
export const NAME_SPLITS = ['none', 'couple_heading', 'couple_cell'] as const;
export const NameSplit = z.enum(NAME_SPLITS);
export type NameSplit = z.infer<typeof NameSplit>;

/**
 * The anchor kinds an identifier column can be written as — the four new ones
 * from §1.5 plus `handle`, which is what a LinkedIn URL already was. Every one of
 * them is in `PERSON_ANCHOR_KINDS`; this list is the narrower set an **import**
 * may propose, because the rest of that vocabulary describes a person rather than
 * identifying them.
 */
export const IDENTIFIER_ANCHOR_KINDS = [
  'email',
  'phone',
  'member_number',
  'external_id',
  'handle',
] as const;
export const IdentifierAnchorKind = z.enum(IDENTIFIER_ANCHOR_KINDS);
export type IdentifierAnchorKind = z.infer<typeof IdentifierAnchorKind>;

/**
 * **The closed transform list** (§2.2). The planner picks one; the executor
 * implements each exactly once. Parameterised as objects rather than as the
 * note's `split_list(',')` shorthand, because the plan is JSON the office edits
 * in a table and a separator hidden inside a string is not editable.
 *
 * Nothing here is a general expression language, and that is the point: a wrong
 * plan must be visible and a wrong executor must be a bug.
 */
export const TRANSFORM_KINDS = [
  'split_list',
  'strip_state_suffix',
  'date',
  'phone_digits',
  'pad_left',
  'checkbox',
  'split_orgs',
  'place_suffix',
  'respace',
] as const;

export const Transform = z.discriminatedUnion('kind', [
  z
    .object({
      kind: kindOf(TRANSFORM_KINDS, 'split_list'),
      separator: z.string().min(1).default(','),
    })
    .strict(),
  /** "Boston, MA" → "Boston": the board's `Club Affiliations` carry state tails. */
  z.object({ kind: kindOf(TRANSFORM_KINDS, 'strip_state_suffix') }).strict(),
  /**
   * A stated format rather than a guessed one — the `updates` sheet's
   * `DD/MonthName/YYYY  hh:mm:ss AM` is the case a generic reader lies about
   * (§5). It is why a **prose** target carries transforms too: that sheet's date
   * is the `content_date` of the note the same row holds.
   */
  z.object({ kind: kindOf(TRANSFORM_KINDS, 'date'), format: z.string().min(1) }).strict(),
  /** The board's phones are integers in the export; the anchor normalizer reads the digits as E.164. */
  z.object({ kind: kindOf(TRANSFORM_KINDS, 'phone_digits') }).strict(),
  /** Zero-padded member numbers, whose leading zeros the export dropped. */
  z
    .object({ kind: kindOf(TRANSFORM_KINDS, 'pad_left'), width: z.number().int().positive() })
    .strict(),
  /** A tick column: this string means yes, anything else means no. */
  z.object({ kind: kindOf(TRANSFORM_KINDS, 'checkbox'), truthy: z.string().min(1) }).strict(),
  /**
   * **One cell naming several organizations is several organizations** (§1.16b)
   * — "Golden State Warriors & Dallas Mavericks", "Braves, Royals, Athletics,
   * White Sox", "Caldera + Lab; DeepIntent".
   *
   * No parameter, and that is the whole difference from `split_list`. One cell
   * of the 2026-09-15 Punta Brava board mixes its separators, so no single
   * separator string reads the column; and the guards that keep "Barnes &
   * Noble", "Ernst & Young LLP", "Procter and Gamble" and "Wells Fargo &
   * Company" one organization are the rule, not a character the office can edit
   * in a table. The reading lives in one function the executor and the profiler
   * both call, so what the plan proposes and what the count says are the same
   * reading.
   *
   * Refused anywhere but an organization-valued fact field — {@link ColumnTarget}
   * is where that is enforced, for the reason the `field` word is enforced there.
   */
  z.object({ kind: kindOf(TRANSFORM_KINDS, 'split_orgs') }).strict(),
  /**
   * **A place glued to the name it qualifies becomes that organization's
   * location** — "LakesideCountryClub,Houston", "PiedmontDrivingClub.GA",
   * "Tallgrass Golf Club, NY". The 2026-09-15 Punta Brava board wrote every
   * club this way, and `split_orgs` read "Houston" as a second organization.
   *
   * The place is not dropped and not kept in the name: it goes on the reference
   * as `anchors.location`, which is what the resolver compares before it binds
   * a name to the one live record of that name — a different city on both sides
   * is a namesake and a question, not a silent bind (`org-anchor-match.ts`).
   *
   * A plan-level statement about the column rather than a guess per cell: a bare
   * comma before one word ("…,Cabo") is a place **because the plan says this
   * column writes places that way** — "3M,GE,IBM" is the column it must not be
   * put on. Runs before any split, which is the only order in which the tail is
   * still beside the name it belongs to.
   */
  z.object({ kind: kindOf(TRANSFORM_KINDS, 'place_suffix') }).strict(),
  /**
   * **Words written without their spaces, given them back** — "DelMarCountryClub"
   * → "Del Mar Country Club", "TroubadourGolf&FieldClub" → "Troubadour Golf &
   * Field Club", "CountryClubOfBirmingham" → "Country Club of Birmingham". A
   * value that already holds a space is left alone.
   *
   * A column-level choice for the same reason as `place_suffix`: one value cannot
   * tell a squashed club name from a brand that is spelled that way ("PayPal",
   * "GoodRx"), and the planner reading the whole column can.
   */
  z.object({ kind: kindOf(TRANSFORM_KINDS, 'respace') }).strict(),
]);

/**
 * **The transforms that read what an organization cell means**, and so go only
 * on a column that writes one ({@link ColumnTarget}, {@link PlanIdentifier}).
 */
export const ORG_ONLY_TRANSFORM_KINDS: readonly Transform['kind'][] = [
  'split_orgs',
  'place_suffix',
  'respace',
];
export type Transform = z.infer<typeof Transform>;

/**
 * One identifier column. `namespace` is what makes an `external_id` mean
 * anything — `monday:<account>`, `linkedin` — and is optional here rather than
 * required on those two kinds because the sketch leaves it so: a namespace the
 * planner could not name is better as an absence the office fills in than as a
 * plan that will not parse.
 *
 * `transforms` is not in the note's sketch of this object and is here because the
 * note's own worked example needs it: the board's `Member ID` is zero-padded to
 * four and its `Phone` is an integer in the export. The phone is settled by the
 * anchor normaliser now (E.164, whoever writes it), but the padding is
 * deliberately **not** — whether `0142` and `142` are the same member is the
 * club's fact, not ours — so it has to be sayable in the plan.
 */
export const PlanIdentifier = z
  .object({
    column: ColumnRef,
    anchor: IdentifierAnchorKind,
    namespace: z.string().min(1).optional(),
    transforms: z.array(Transform).min(1).optional(),
  })
  .strict()
  /* **`split_orgs` is refused here too, and it has to be said twice because the
     transforms are declared twice** (§1.16b). {@link ColumnTarget} keeps the
     transform to a column that writes an organization; this object is not a
     column target and carries its own `transforms`, so without this an
     identifier column would be the one place in the plan the rule did not
     reach. A member number that happened to hold an "&" would be read as two
     organizations and written as two anchors under one member — the widest
     failure this transform has, because an anchor is identity. */
  .superRefine((identifier, ctx) => {
    const orgOnly = (identifier.transforms ?? []).find((one) =>
      ORG_ONLY_TRANSFORM_KINDS.includes(one.kind),
    );
    if (!orgOnly) return;
    ctx.addIssue({
      code: 'custom',
      path: ['transforms'],
      message:
        orgOnly.kind === 'split_orgs'
          ? 'split_orgs reads a cell as several organizations; an identifier column writes one anchor'
          : `${orgOnly.kind} reads an organization cell; an identifier column writes one anchor`,
    });
  });
export type PlanIdentifier = z.infer<typeof PlanIdentifier>;

export const ImportPlanIdentity = z
  .object({
    name: NameRule,
    split: NameSplit,
    identifiers: z.array(PlanIdentifier),
  })
  .strict();
export type ImportPlanIdentity = z.infer<typeof ImportPlanIdentity>;

/**
 * **The three levels a person a file produces can arrive at** (§1.3): a
 * first-class `subject`, a `known` person (an entity with identifiers and the
 * facts the row states, no subject record, no status — what a spouse is today),
 * or `set_aside`, which is not written at all and kept on the session.
 */
export const RESOLUTION_LEVELS = ['subject', 'known', 'set_aside'] as const;
export const ResolutionLevel = z.enum(RESOLUTION_LEVELS);
export type ResolutionLevel = z.infer<typeof ResolutionLevel>;

/**
 * A per-group or per-column override of the default level — see
 * {@link RULE_MATCH_KINDS} for why the two are told apart by `by`.
 */
export const PlanResolutionRule = z.discriminatedUnion('by', [
  z.object({ ...columnMatch, level: ResolutionLevel }).strict(),
  z.object({ ...groupMatch, level: ResolutionLevel }).strict(),
]);
export type PlanResolutionRule = z.infer<typeof PlanResolutionRule>;

export const ImportPlanResolution = z
  .object({ default: ResolutionLevel, rules: z.array(PlanResolutionRule) })
  .strict();
export type ImportPlanResolution = z.infer<typeof ImportPlanResolution>;

/**
 * What preview does with a person who turns out to be a **user of this system**
 * (§1.14): propose the vertical's membership word (the club's `member`), propose
 * the plan's own default, or nothing. The match itself — email and normalized
 * name against the org's members — is the executor's, and the link it sets is
 * `org_membership.person_entity_id`.
 */
export const USER_MATCH_MODES = ['propose_member', 'propose_default', 'off'] as const;
export const UserMatchMode = z.enum(USER_MATCH_MODES);
export type UserMatchMode = z.infer<typeof UserMatchMode>;

export const PlanStatusRule = z.discriminatedUnion('by', [
  z.object({ ...columnMatch, status: SubjectStatus }).strict(),
  z.object({ ...groupMatch, status: SubjectStatus }).strict(),
]);
export type PlanStatusRule = z.infer<typeof PlanStatusRule>;

/**
 * The status a subject arrives with, and the rules that override it per group.
 * The words are the **org's vertical's** vocabulary, and this package keeps that
 * as a loose union (§0.7): a `prospect` rule on a business org is refused
 * server-side, where the vertical is known, not here.
 */
export const ImportPlanStatus = z
  .object({
    default: SubjectStatus,
    rules: z.array(PlanStatusRule),
    user_match: UserMatchMode,
  })
  .strict();
export type ImportPlanStatus = z.infer<typeof ImportPlanStatus>;

/** An occasion is an `event` entity today, and the list exists to be widened. */
export const OCCASION_KINDS = ['event'] as const;
export const OccasionKind = z.enum(OCCASION_KINDS);
export type OccasionKind = z.infer<typeof OccasionKind>;

/**
 * **A file that is an occasion writes one fact about everyone on it** (§1.3a):
 * the executor resolves the event entity and writes `event_participation` with
 * the file as evidence for every person on the list — created, known, or already
 * on record. A contact source carries `occasion: null` and writes nothing about
 * people it did not create.
 */
export const ImportOccasion = z
  .object({
    name: z.string().min(1),
    /** The date at the grain the file stated it, as every window bound is. */
    date: LooseDate.optional(),
    kind: OccasionKind,
  })
  .strict();
export type ImportOccasion = z.infer<typeof ImportOccasion>;

/**
 * **The three things a person can be to a row that is an occasion** (owner's
 * ruling, 2026-09-07).
 *
 * `host` is whoever the row is booked under — the member on a reservation, the
 * seat-holder at a dinner. `guest` is whoever they brought. `participant` is the
 * honest word for a row that names several people and says nothing about which
 * of them stood where: a tournament pairing, an introduction log.
 *
 * The word travels twice: it is the `role` field of the `event_participation`
 * fact each person gets, so the record says what they were at the occasion, and
 * it is what {@link ImportRowEvent.tie} reads to know which end of the row the
 * tie runs from.
 */
export const ROW_EVENT_ROLES = ['host', 'guest', 'participant'] as const;
export const RowEventRole = z.enum(ROW_EVENT_ROLES);
export type RowEventRole = z.infer<typeof RowEventRole>;

/**
 * **The occasion's name: a constant, or a column that names one per row.**
 *
 * Both spellings are real files. A reservations sheet has no occasion column at
 * all and every row is a `"Reservation"`; a function-room log has a `Function`
 * column whose cell is `Wine dinner` on one row and `Board lunch` on the next.
 * A string is the first and a {@link ColumnRef} is the second, and the two
 * cannot be confused for one another — one is text and the other an object,
 * which is the same reason a prose target's `date_from` may be either.
 */
export const RowEventName = z.union([z.string().min(1), ColumnRef]);
export type RowEventName = z.infer<typeof RowEventName>;

/**
 * **What the row is an occasion of** — the name, the column the date is read
 * from, and the kind of node it becomes.
 *
 * `date` is a {@link ColumnRef} and not a {@link LooseDate}, which is the whole
 * difference from {@link ImportOccasion}: a file's occasion is *one* occasion
 * with one date, and a file of occasions has a date per row. It is nullable
 * rather than optional for the reason `occasion` itself is — "these rows carry
 * no date" is a thing the planner decided, and a key that could simply be
 * missing would make a plan that never considered it indistinguishable from one
 * that did.
 */
export const RowEventOccasion = z
  .object({
    name: RowEventName,
    date: ColumnRef.nullable(),
    kind: OccasionKind,
  })
  .strict();
export type RowEventOccasion = z.infer<typeof RowEventOccasion>;

/**
 * **One column of names, and what the people in it were to the row.**
 *
 * `identifiers` is the same shape the plan's own identity rule uses and is here
 * for the same reason: a reservations sheet carries a `Member ID` beside the
 * member's name, and without it the ten rows one member hosts are ten people
 * with one name rather than one member who hosted ten times. A guest column
 * usually carries none, and folds by name instead.
 *
 * `level` is per column and not per file, because the two halves of such a row
 * are rarely the same thing to the club: the member is somebody they track and
 * the guest is somebody they now know of. `status` is read only where the level
 * is `subject`, exactly as it is everywhere else.
 */
export const RowEventPerson = z
  .object({
    column: ColumnRef,
    role: RowEventRole,
    identifiers: z.array(PlanIdentifier).optional(),
    level: ResolutionLevel,
    status: SubjectStatus.optional(),
  })
  .strict();
export type RowEventPerson = z.infer<typeof RowEventPerson>;

/**
 * **A file whose rows are things that happened between named people at a time**
 * (owner's ruling, 2026-09-07) — a reservation history, a dinner seating, a
 * tournament pairing, an introduction log.
 *
 * Until this, the only honest answer such a file could get was the sixth column
 * target: the planner said in one sentence what the column held and nothing was
 * written. It is a common enough shape, and a structured one, that it is worth
 * carrying at structured-row cost — **no model call per row**, the same
 * deterministic executor as every other plan.
 *
 * **What one row produces:**
 *
 *  - **one `event` entity**, named `<name> · <date>` where the row states a date
 *    and `<name>` where it does not, deduplicated within the org and within the
 *    session on the pair (normalised name, date). Ten Tuesdays are ten events;
 *    two rows on one Tuesday are one.
 *  - **one person record per {@link people} column whose cell on this row is not
 *    empty**, at the locator `<row>#<role>` — the same `#n` shape a couple
 *    heading's two halves take, for the same reason: two records off one row of
 *    the file. Each is identified by its own `identifiers` where it has them and
 *    by its exact name where it does not, and arrives at that column's `level`.
 *    An empty cell writes nobody; a row with no guest is a member dining alone.
 *  - **one `event_participation`** for each of those people, carrying the role
 *    in its fields, the row as its evidence, first-party as `club_supplied` —
 *    the same claim path every other import fact takes.
 *  - **a `social_tie`** between the host and every guest on the row, where
 *    {@link tie} is set: its `context` is the tie's own words ("hosted at the
 *    club") and its `since` is the row's date. A row with no host, or a plan
 *    with no tie, writes none.
 *
 * **The same person on ten rows is one person.** Person records are keyed
 * across the file by identifier where there is one and by normalised name where
 * there is not; the repeats fold onto the first, and the participations
 * accumulate on the survivor rather than being dropped with it. Which is also
 * why a repeated name here is **not** the `duplicate_name` question §1.5 asks of
 * a contact list: a guest list saying `Ada Wren` twice is one guest who came
 * twice, and asking the office about each of sixty rows would be asking them to
 * confirm the file's own shape.
 *
 * **The plan's `identity.name` is ignored when this is set**, and so are its
 * `columns`. Each people column is its own name source, so there is no one name
 * rule to read; and a cell on a row that is an occurrence belongs to the
 * occurrence rather than to any one of the names on it — a party size is about
 * the booking, and writing it onto the guest would be the executor inventing an
 * attribution the file never made. The plan's own place for such a column is
 * `ignore` with a reason, which is exactly what it says. `resolution` is not
 * read either, for a third form of the same reason: the level belongs to the
 * *column*, because the member is somebody the club tracks and the guest is
 * somebody it now knows of, and one default over the file could not say both.
 *
 * `record_rule`, `exclusions`, `status.default` — the fallback for a people
 * column naming no status of its own — `status.user_match` and `bin` are
 * unchanged and still read: which rows are rows, and what happens to a name that
 * is not one, are the same questions here as anywhere.
 */
export const ImportRowEvent = z
  .object({
    occasion: RowEventOccasion,
    /** At least one — a row event with nobody on it is not one. */
    people: z.array(RowEventPerson).min(1),
    tie: z
      .object({ context: z.string().min(1) })
      .strict()
      .nullable(),
  })
  .strict();
export type ImportRowEvent = z.infer<typeof ImportRowEvent>;

/**
 * **Operational columns, classified** (§1.13). `membership` defaults to a record
 * field; `commercial` and `workflow` default to `ignore` and reach a record field
 * only when the office ticks the exception. Money and workflow stay out of the
 * record unless somebody says otherwise, and dead columns are `ignore` with a
 * reason rather than a fourth group.
 */
export const RECORD_FIELD_GROUPS = ['membership', 'commercial', 'workflow'] as const;
export const RecordFieldGroup = z.enum(RECORD_FIELD_GROUPS);
export type RecordFieldGroup = z.infer<typeof RecordFieldGroup>;

/**
 * **A record field a group or a column sets on every row it matches**, rather
 * than one a column's own cell fills.
 *
 * §1.15 is the case it exists for: a resigned member is a contact who *was* one,
 * and the board says so twice over — with a `Resigned Date` column, whose cell is
 * the field's value, and with the group the row sits in. A file carrying only the
 * group has no cell to read, and without this rule the plan could not say
 * "everyone in Resigned is a former member" at all: the office would have had to
 * tick 3 rows by hand, or the executor would have needed a word about statuses
 * that only ever meant this.
 *
 * `value` is a string or a boolean and nothing else, because those are the two
 * things a rule of this shape has to say — a date is what the *column* target is
 * for, and a number that is not on the record's own vocabulary is money.
 */
const RecordFieldRuleValue = z.union([z.string(), z.boolean()]);

export const PlanRecordFieldRule = z.discriminatedUnion('by', [
  z.object({ ...columnMatch, key: z.string().min(1), value: RecordFieldRuleValue }).strict(),
  z.object({ ...groupMatch, key: z.string().min(1), value: RecordFieldRuleValue }).strict(),
]);
export type PlanRecordFieldRule = z.infer<typeof PlanRecordFieldRule>;

/** The one split a prose column can carry: the board's `YY/MM/DD (Author):` log. */
export const PROSE_SPLITS = ['dated_log'] as const;
export const ProseSplit = z.enum(PROSE_SPLITS);
export type ProseSplit = z.infer<typeof ProseSplit>;

/**
 * Where a split entry's `content_date` comes from: the stamp the entry carries,
 * or another column of the same row. Absent is `absent` basis — never inferred.
 *
 * The word and a {@link ColumnRef} cannot be confused for one another — one is a
 * string and the other an object — which is the same reason the group is its own
 * arm of {@link RULE_MATCH_KINDS} rather than a reserved column name.
 */
export const PROSE_DATE_FROM_STAMP = 'stamp';
export const ProseDateFrom = z.union([z.literal(PROSE_DATE_FROM_STAMP), ColumnRef]);
export type ProseDateFrom = z.infer<typeof ProseDateFrom>;

/**
 * The two prose kinds an import writes. `note` is a staff note and is extracted;
 * `reference` is the earlier tool's dossier, which is kept as context and
 * **never promoted to facts** (§1.12).
 */
export const IMPORT_PROSE_KINDS = ['note', 'reference'] as const;
export const ImportProseKind = z.enum(IMPORT_PROSE_KINDS);
export type ImportProseKind = z.infer<typeof ImportProseKind>;

/**
 * **One prefix, and what a cell starting with it is.** `extract` is optional and
 * defaults from this entry's own `kind` — a routed `reference` is not read unless
 * the plan says to read it, exactly as a plain one is not (§1.12).
 */
export const ProseKindRoute = z
  .object({
    starts_with: z.string().min(1),
    kind: ImportProseKind,
    extract: z.boolean().optional(),
  })
  .strict();
export type ProseKindRoute = z.infer<typeof ProseKindRoute>;

/**
 * **A prose kind chosen by what the cell starts with** — the `updates` sheet
 * (§2.2): `Update Content` is one column holding two things, a pasted dossier
 * behind a `Dossier:` prefix and a staff note otherwise, and the office would
 * have to split the sheet in half by hand to say that any other way.
 *
 * A prefix rather than a pattern, for the reason the resolution rules match on
 * equality: the plan is a document a person reads and edits, and a regular
 * expression in it is not something the office can check by eye. First match in
 * order wins; a cell matching none is the `default`.
 */
export const ProseKindRouting = z
  .object({
    default: ImportProseKind,
    /** At least one — a routing with no prefixes is the plain kind, spelled long. */
    by_prefix: z.array(ProseKindRoute).min(1),
  })
  .strict();
export type ProseKindRouting = z.infer<typeof ProseKindRouting>;

/** A plain kind for the column, or one chosen per cell by prefix. */
export const ProseKindRule = z.union([ImportProseKind, ProseKindRouting]);
export type ProseKindRule = z.infer<typeof ProseKindRule>;

/**
 * The same rule with every `extract` filled in — what {@link ColumnTarget}
 * resolves a prose target's kind to, so no reader downstream re-derives a default
 * that costs money to get wrong.
 */
export type ResolvedProseKind =
  | ImportProseKind
  | {
      default: ImportProseKind;
      by_prefix: Array<{ starts_with: string; kind: ImportProseKind; extract: boolean }>;
    };

export const RELATION_TARGET_TYPES = ['family_relation', 'social_tie'] as const;

/**
 * How the other end of a relation column is found: a name that is another record
 * **in this file** (the board's `PB Network Relationships`), or a satellite
 * person minted for the name as research already does (the `Spouse` column).
 */
export const RELATION_RESOLVES = ['in_file_name', 'satellite_person'] as const;
export const RelationResolve = z.enum(RELATION_RESOLVES);
export type RelationResolve = z.infer<typeof RelationResolve>;

/**
 * **What shape the column wanted to be**, where the planner can name one — a
 * hint for whoever reads the file's shape next, and nothing this build executes.
 *
 * Four words and no more, because the list is a filing aid rather than a
 * vocabulary anything depends on: `row_event` is a row that is an occurrence
 * (a reservation, a booking, a round played), `interaction` is a contact between
 * two people, `membership` is a standing in something the plan has no field for,
 * and `other` is the honest answer to everything else. A hint the planner cannot
 * give is simply absent; it is never guessed.
 */
export const UNSUPPORTED_SHAPE_HINTS = ['row_event', 'interaction', 'membership', 'other'] as const;
export const UnsupportedShapeHint = z.enum(UNSUPPORTED_SHAPE_HINTS);
export type UnsupportedShapeHint = z.infer<typeof UnsupportedShapeHint>;

/**
 * **How long the planner's sentence about a column may run.** One sentence is
 * the whole ask — "each row is a member hosting a named guest on a date" — and
 * the office reads it in a list beside five others, so a paragraph here is a
 * paragraph nobody reads. It is also the field the escape hatch mails, and a
 * bound on it is a bound on that.
 */
export const UNSUPPORTED_MEANING_MAX = 200;

/**
 * The six things a column can become. Discriminated on `kind`, so a target the
 * planner got wrong is reported against **its own arm** — the planner chunk feeds
 * these issues back to the model, and a union error naming all six arms would
 * be six wrong answers and one right one.
 *
 * **`ignore` and `unsupported` are not the same answer, and the difference is
 * the whole reason the sixth arm exists** (owner's ruling, 2026-09-07):
 *
 *  - **`ignore`** — *we know what it is and choose not to import it.* Dues, a
 *    to-do list, a last-contacted stamp, a dead column: the office reads the
 *    reason and agrees. Nothing is owed to anybody.
 *  - **`unsupported`** — *we know what it is and cannot import it yet.* The
 *    planner can say in a sentence what the column holds and what it ought to
 *    become, and this plan has no shape for it — a reservation row that is an
 *    occurrence, a column of interactions, a standing the record has no field
 *    for. Filing that under `ignore` writes off a gap in the product as a
 *    decision the office made, and the file is then the only place the gap is
 *    written down.
 *
 * **The applier treats an `unsupported` target exactly as it treats `ignore`:
 * nothing is written.** They differ in what is said about them, not in what
 * happens — the Plan tab lists them under their own heading with the planner's
 * sentence, the pre-commit summary counts them, and the office may still change
 * one to any other kind if they decide it is a detail, or an ignore after all.
 * A commit is never blocked by one.
 */
const ColumnTargetShape = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('fact'),
      type: FactType,
      /**
       * **The catalog field the cell fills** — `org` on an `employment`, `place`
       * on a `residence`.
       *
       * One of {@link FACT_TYPE_FIELDS} for the chosen `type`, and refused
       * otherwise — {@link ColumnTarget} is where that is enforced, for the
       * reason the relation arm's `relation` is enforced there. A free string
       * was the last place in the plan where a target nothing can execute still
       * parsed: `{type: 'employment', field: 'employer'}` reads perfectly and
       * maps to nothing, because the catalog's word is `org`. The planner is
       * shown the same vocabulary and the Plan tab offers it as a select, so
       * every writer of a plan now draws from one list.
       */
      field: z.string().min(1),
      /**
       * **A list, and named for it**, where the note's sketch spells one
       * `transform?`. Its own worked example applies two to one column — the
       * board's `Club Affiliations` is `split_list` *then* `strip_state_suffix` —
       * so a singular field could not express the case it was written for. They
       * run in order; one transform is a list of one.
       */
      transforms: z.array(Transform).min(1).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('record_field'),
      key: z.string().min(1),
      group: RecordFieldGroup,
    })
    .strict(),
  z
    .object({
      kind: z.literal('prose'),
      split: ProseSplit.optional(),
      /** `'stamp'` or a {@link ColumnRef}; see {@link ProseDateFrom}. */
      date_from: ProseDateFrom.optional(),
      /**
       * **The list the fact arm has, for the same reason and one more.** A prose
       * column's own cell needs no transform, but the column the date comes from
       * does: the `updates` sheet's `Created At` is
       * `DD/MonthName/YYYY  hh:mm:ss AM`, which a generic reader gets wrong, so
       * the `date` transform states the format `date_from` is read with.
       */
      transforms: z.array(Transform).min(1).optional(),
      prose_kind: ProseKindRule,
      /**
       * Whether the notes are read at commit. **Absent takes the kind's
       * default** — see {@link ColumnTarget}, which fills it — so a plan that
       * says nothing about a dossier column does not accidentally pay to extract
       * 452 of them. On a routed kind it is the default for cells no prefix
       * claimed, and each prefix carries its own.
       */
      extract: z.boolean().optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('relation'),
      type: z.enum(RELATION_TARGET_TYPES),
      /**
       * Which relation it is — `spouse`, `child`. **Required for
       * `family_relation`** and refused for `social_tie`, whose flavour is
       * carried by `context` instead; {@link ColumnTarget} is where that is
       * enforced.
       */
      relation: z.string().min(1).optional(),
      resolve: RelationResolve,
      /** The tie's own words: "PB network relationship". */
      context: z.string().min(1).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('seat'),
      /**
       * **Which part of the seat the cell holds** (docs/notes/2026-09-29-event-seating-spec.md):
       * `whole` for a cell holding the full assignment. Only an attendee list
       * keeps a seat; the planner refuses one on any other import, and the
       * applier writes nothing for it there.
       */
      part: z.enum(SEAT_PARTS),
    })
    .strict(),
  z.object({ kind: z.literal('ignore'), reason: z.string().min(1) }).strict(),
  z
    .object({
      kind: z.literal('unsupported'),
      /**
       * **The planner's own sentence about the column** — what it holds and what
       * it should become, in the words a person reads: "each row is a member
       * hosting a named guest on a date".
       *
       * Required and bounded ({@link UNSUPPORTED_MEANING_MAX}). A target saying
       * only "unsupported" is `ignore` with a longer name: the sentence is the
       * entire value of the arm, because it is what the office reads, what the
       * escape hatch mails, and what the next build of the plan contract is
       * written against.
       */
      meaning: z.string().min(1).max(UNSUPPORTED_MEANING_MAX),
      /** Where it would belong if the plan had a shape for it, where that is sayable. */
      shape_hint: UnsupportedShapeHint.optional(),
    })
    .strict(),
]);

type ColumnTargetIn = z.infer<typeof ColumnTargetShape>;
type ProseTargetIn = Extract<ColumnTargetIn, { kind: 'prose' }>;

/**
 * A column target with the things the sketch implies settled: a prose target's
 * `extract` is **resolved** (true for a note, false for a reference), on the
 * column and on every prefix of a routed kind, so every reader downstream sees a
 * boolean rather than re-deriving a default that costs money to get wrong; a
 * `family_relation` with no `relation` does not parse; and neither does a
 * `social_tie` **with** one.
 *
 * All of it lives here rather than inside the arms because zod's discriminated
 * union takes plain objects only — a refinement or a default on an arm would have
 * cost the per-arm error reporting the planner needs.
 */
export type ColumnTarget =
  | Exclude<ColumnTargetIn, { kind: 'prose' }>
  | (Omit<ProseTargetIn, 'prose_kind'> & { prose_kind: ResolvedProseKind; extract: boolean });

/** The kind a cell no prefix claimed gets — the whole column's, or the routing's. */
const defaultProseKind = (rule: ProseKindRule): ImportProseKind =>
  typeof rule === 'string' ? rule : rule.default;

/**
 * **The catalog's word for a field holding an organization**, and the types that
 * have one, derived rather than listed.
 *
 * `split_orgs` is the one transform that is about *what the cell means* and not
 * about how it is punctuated, so it is the one transform with a place it belongs:
 * a column that writes an organization. Reading the set off
 * {@link FACT_TYPE_FIELDS} is what keeps it true — a type that gains an `org`
 * field gains the transform with it, and nobody has to remember this line.
 */
const ORG_REF_FIELD = 'org';
const ORG_VALUED_FACT_TYPES: readonly FactType[] = (
  Object.keys(FACT_TYPE_FIELDS) as FactType[]
).filter(
  (type) => isImportTargetableFactType(type) && FACT_TYPE_FIELDS[type].includes(ORG_REF_FIELD),
);

/**
 * Whether a fact target writes an organization — an `employment`'s `org`, a
 * `board_seat`'s, a membership's, an investment's, a financial event's. The
 * plan contract refuses `split_orgs` anywhere else, and the stub planner
 * proposes it nowhere else.
 */
export const isOrgValuedFactField = (type: FactType, field: string): boolean =>
  field === ORG_REF_FIELD && ORG_VALUED_FACT_TYPES.includes(type);

/**
 * **Why this type cannot come from a column**, in the office's words and per
 * type rather than one sentence stretched over four.
 *
 * `org_relation` names two entities and a row supplies one value against one
 * person; `org_profile` is about an organisation and a row's subject is a
 * person; `broker_exposure` and `nationality` are refused on the *value* — one
 * is a measurement nobody typed, the other is a code where a column holds a
 * nationality's name. The default is deliberately vague rather than wrong: a
 * fifth type added to the list without a sentence here still refuses, and says
 * only what it knows.
 */
const untargetableReason = (type: FactType): string => {
  switch (type) {
    case 'org_relation':
      return 'both sides of it are organizations or events, and a column names one value against one person';
    case 'org_profile':
      return 'it records what an organization is, and every row here is about a person';
    case 'event_profile':
      return 'it records what an event is, and every row here is about a person';
    case 'broker_exposure':
      return 'every field of it is something a data broker was read for, not something anybody types into a spreadsheet';
    case 'onchain_exposure':
      return 'every field of it is something on-chain records were read for, not something anybody types into a spreadsheet';
    case 'account_exposure':
      return 'every field of it is something an account lookup was run for, not something anybody types into a spreadsheet';
    case 'nationality':
      return 'it holds a two-letter country code, and a nationality column holds words like “Irish” — which would refuse every row it is written on';
    default:
      return 'the catalog does not accept this type from a spreadsheet column';
  }
};

/**
 * **Fields of a targetable type that a column still may not fill** (2026-10-08).
 * A residence's value is what deep research read off a property record or a
 * listing; a spreadsheet's "home value" column is nobody's sourced figure, and
 * the net-worth estimate would read it as one.
 */
const IMPORT_UNTARGETABLE_FACT_FIELDS: Partial<Record<FactType, readonly string[]>> = {
  residence: ['value', 'value_kind', 'value_date'],
};

/** The fields of `type` a column may fill: the catalog's, less the ones above. */
export const importTargetableFields = (type: FactType): readonly string[] =>
  FACT_TYPE_FIELDS[type].filter(
    (field) => !(IMPORT_UNTARGETABLE_FACT_FIELDS[type] ?? []).includes(field),
  );

export const ColumnTarget = ColumnTargetShape.superRefine((target, ctx) => {
  /* **And a type an import can actually write.** Four catalog types cannot be
     pointed at from a column (`IMPORT_UNTARGETABLE_FACT_TYPES`), and they are
     refused for three different reasons — so the sentence names the reason
     rather than asserting one of them about all four. It used to say "both
     sides of it are organizations or events", which was true of `org_relation`
     alone and became false as each of the other three arrived; an operator
     pointed here learns nothing from a reason that is not their type's.

     Refused here rather than narrowed in the arm's `type` so the message can
     say why at all — the type exists and is on the catalog, and this is the one
     place it cannot be named. */
  if (target.kind === 'fact' && !isImportTargetableFactType(target.type)) {
    ctx.addIssue({
      code: 'custom',
      path: ['type'],
      message: `${target.type} cannot be imported from a column — ${untargetableReason(target.type)}`,
    });
    return;
  }
  /* **A fact target names a field the catalog has.** Same posture as the two
     relation rules below and the same reason: the docblock on the field promises
     it, and a promise the schema does not keep is a column that parses, saves,
     previews and then writes nothing at all. */
  if (target.kind === 'fact' && !isFactTypeField(target.type, target.field)) {
    ctx.addIssue({
      code: 'custom',
      path: ['field'],
      message: `${target.type} has no field '${target.field}' — one of ${FACT_TYPE_FIELDS[
        target.type
      ].join(', ')}`,
    });
  } else if (
    target.kind === 'fact' &&
    !importTargetableFields(target.type).includes(target.field)
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['field'],
      message: `${target.type} ${target.field} comes from research, not from a spreadsheet column — one of ${importTargetableFields(target.type).join(', ')}`,
    });
  }
  /* **`split_orgs` reads a cell as several organizations, so it belongs on a
     column that writes one** (§1.16b). Everywhere else it is a transform that
     would silently multiply a value nobody meant as a list: an email column
     split on its comma, a note cut at a semicolon, a residence turned into two
     places. The other six transforms are about punctuation and can sit anywhere
     the office puts them; this one is about what the cell *means*, and the one
     thing it means is an organization. */
  const transforms = 'transforms' in target ? (target.transforms ?? []) : [];
  if (
    transforms.some((one) => one.kind === 'split_orgs') &&
    !(target.kind === 'fact' && isOrgValuedFactField(target.type, target.field))
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['transforms'],
      message: `split_orgs reads a cell as several organizations, so it goes on a column that writes one — the 'org' field of ${ORG_VALUED_FACT_TYPES.join(', ')}`,
    });
  }
  /* **`split_list` and `split_orgs` are two readings of the same cell, and the
     first one destroys the second** (§1.16b). The board's `Club Affiliations`
     is "Stonecreek Club, CO, Tallgrass Golf Club, WY": `split_list` on the
     comma hands `split_orgs` four shreds, two of which are state codes, and the
     organization reading — which is the one that knows `CO` belongs to the club
     in front of it — never sees the cell it was put there to read. They are not
     complementary and stacking them is always a mistake, whichever order they
     are written in, so the plan may not say both. */
  /* `place_suffix` and `respace` are organization readings too — a glued place
     is an organization's location, and a squashed word is a squashed name — so
     they are held to the same column. */
  const orgOnlyElsewhere = transforms.find(
    (one) => one.kind !== 'split_orgs' && ORG_ONLY_TRANSFORM_KINDS.includes(one.kind),
  );
  if (
    orgOnlyElsewhere &&
    !(target.kind === 'fact' && isOrgValuedFactField(target.type, target.field))
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['transforms'],
      message: `${orgOnlyElsewhere.kind} reads an organization cell, so it goes on a column that writes one — the 'org' field of ${ORG_VALUED_FACT_TYPES.join(', ')}`,
    });
  }
  /* **A glued place is read before the cell is cut**, because a split is what
     separates it from the name it belongs to: after `split_orgs`, "Houston" is
     already its own organization. */
  const placeAt = transforms.findIndex((one) => one.kind === 'place_suffix');
  const splitAt = transforms.findIndex(
    (one) => one.kind === 'split_orgs' || one.kind === 'split_list',
  );
  if (placeAt >= 0 && splitAt >= 0 && placeAt > splitAt) {
    ctx.addIssue({
      code: 'custom',
      path: ['transforms'],
      message:
        'place_suffix goes before the split — once the cell is cut, the place is a piece of its own and no longer beside the name it belongs to',
    });
  }
  /* **And `respace` after it**: it leaves a value holding a space alone, and a
     cell naming several clubs holds the ", " between them — so before the split
     it reads the whole cell as spaced and does nothing, with a plan that says
     it did. */
  const respaceAt = transforms.findIndex((one) => one.kind === 'respace');
  if (respaceAt >= 0 && splitAt >= 0 && respaceAt < splitAt) {
    ctx.addIssue({
      code: 'custom',
      path: ['transforms'],
      message:
        'respace goes after the split — before it, the cell still holds the ", " between names and is left as it is',
    });
  }
  if (
    transforms.some((one) => one.kind === 'split_orgs') &&
    transforms.some((one) => one.kind === 'split_list')
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['transforms'],
      message:
        'split_list and split_orgs both read one cell as several values — split_orgs reads the organizations itself, and a separator run first hands it shreds',
    });
  }
  if (target.kind !== 'relation') return;
  if (target.type === 'family_relation' && target.relation === undefined) {
    ctx.addIssue({
      code: 'custom',
      path: ['relation'],
      message: "a family_relation target says which relation it is — 'spouse', 'child'",
    });
  }
  /* The docblock on the field promises this, and a promise the schema does not
     keep is a plan that parses and executes as something else: a `social_tie`
     carries its flavour in `context`, and a `relation` beside it would be a word
     the executor has nowhere to put. */
  if (target.type === 'social_tie' && target.relation !== undefined) {
    ctx.addIssue({
      code: 'custom',
      path: ['relation'],
      message: "a social_tie target has no relation — its flavour is the tie's own context",
    });
  }
}).transform((target): ColumnTarget => {
  if (target.kind !== 'prose') return target;
  const rule = target.prose_kind;
  return {
    ...target,
    prose_kind:
      typeof rule === 'string'
        ? rule
        : {
            default: rule.default,
            by_prefix: rule.by_prefix.map((route) => ({
              ...route,
              extract: route.extract ?? route.kind === 'note',
            })),
          },
    extract: target.extract ?? defaultProseKind(rule) === 'note',
  };
});

/**
 * How sure the planner is, in three words and **never a number** — the rule the
 * whole package holds to. It is what the Plan tab sorts the office's attention
 * by: the board's `Club Affiliations` mapping is low and wants a look.
 */
export const PLAN_CONFIDENCES = ['high', 'medium', 'low'] as const;
export const PlanConfidence = z.enum(PLAN_CONFIDENCES);
export type PlanConfidence = z.infer<typeof PlanConfidence>;

export const PlanColumn = z
  .object({
    column: ColumnRef,
    target: ColumnTarget,
    confidence: PlanConfidence,
    /** Why, in the planner's own words — rendered beside the target. */
    note: z.string().min(1).optional(),
  })
  .strict();
export type PlanColumn = z.infer<typeof PlanColumn>;

/**
 * **A second sheet of the same file, attached to the first** (§2.2's `updates`
 * sheet): its rows are not people, they are things said *about* people, and they
 * find their person by an identifier the primary sheet also carries — "joined on
 * `Item ID`", where `on.column` is this sheet's column and `on.matches` is the
 * primary's.
 *
 * **One plan, not two.** The note calls it "a second plan on the same session",
 * and that is what it is for the office — a second table on the Plan tab — but it
 * cannot be a second `import_plan_version`: the version chain is linear and one
 * per session, so two plans would be two chains, two commits and two things to
 * undo for one file. So it is a key on the plan the session already has.
 *
 * A join's columns may carry **any** target, and the identity, record and name
 * rules that apply to them are the **primary's**: a joined row is not a record of
 * its own, it is more of one that already exists. Which is also why there is no
 * `identity` or `resolution` here to get out of step with the plan's.
 */
export const ImportPlanJoin = z
  .object({
    source: ImportPlanSource,
    /**
     * `column` is the joined sheet's, `matches` is the primary's. Named rather
     * than positional because the two sheets rarely spell the id the same way,
     * and a pair of bare column names would leave the office to guess which is
     * which.
     */
    on: z.object({ column: ColumnRef, matches: ColumnRef }).strict(),
    columns: z.array(PlanColumn),
  })
  .strict();
export type ImportPlanJoin = z.infer<typeof ImportPlanJoin>;

/**
 * **The needs-a-look bin** (§1.8): a reason the office is shown next to a row it
 * has to settle. The bin is not a failure — it is a list with one gesture — and
 * it persists after commit so it can be worked later.
 *
 * The first nine are **shape checks over the name**. Seven of them the plan turns
 * on or off, because whether `Bram M.` is a name is a judgement about this file.
 *
 * **`single_token` and `role_name` are not the plan's to turn off** (owner's
 * ruling, 2026-09-08). A board import minted six live people called `Chris`,
 * `Gracie` and the like, because the planner had not listed `single_token` for
 * the guest column and the applier only ran the checks the plan had named. The
 * resolver's own rule has refused a bare given name since the Kelly incident
 * (`identity/first-name.ts`) — there is genuinely no telling Kelly Spencer from
 * Kelly Nguyen out of the word "Kelly" — and a role word names nobody at all.
 * Neither of those is a judgement about a file, so neither is offered as one: the
 * row goes to the bin every time, and the office names it or sets it aside. It is
 * still only the bin — the row never fails and it is never written silently.
 *
 * The next two are **identity outcomes** and the plan does not gate them, because
 * §1.5 does not leave them open: *same name, different identifiers: two people;
 * same identifier: one person; same name, no identifier: the bin.* A row that
 * repeats a name the file carries no identifier for, and a pair whose identifiers
 * say "one person" and "two people" at once, are the two ways a file can fail to
 * answer that question, and neither is a preference. They arrive at the row the
 * same way a shape check does — one reason, one gesture — so they are the same
 * vocabulary rather than a second one beside it.
 *
 * **What answers each reason**, which is not the same question for the two halves
 * of the list (`needsALook`, {@link SetRowBody}):
 *
 *  - the **nine shape checks** are questions about the *name on this row*, so a
 *    name is an answer to them: `{name: 'Vessith Tegey'}` settles the row, and so
 *    does a level — `{level: 'set_aside'}` for "not this one", any other level for
 *    "as it stands". `custom` is a shape check the plan wrote and answers the same
 *    way;
 *  - the **two identity outcomes** are questions about *which person this row is*,
 *    and a name does not answer one: re-stating `Preman Pendton` on a row that
 *    repeats `Preman Pendton` leaves the file saying exactly what it said. What
 *    answers them is a **level** — `{level: 'set_aside'}` to drop the row, or an
 *    explicit level to keep it, which is the office saying "these really are two
 *    people" — or a **pick**, where the row is also `ambiguous` and the office
 *    chose whom it is. A name *may* be sent beside the level, and where it changes
 *    the name it changes the file's answer too; it is simply not the gesture that
 *    settles the row on its own.
 *  - **`sentinel`** is a question about *whether this row is a person at all*,
 *    and it arrives already answered: the row is `set_aside`, so its default is
 *    "no". A level is what overturns that — the office saying "yes, that is
 *    somebody" — and a name beside it works the way it does for a shape check.
 */
export const BIN_RULES = [
  'initials_only',
  'no_last_name',
  'single_token',
  'looks_like_email',
  'looks_like_url',
  'decorated',
  'looks_like_org',
  'unsplit_couple',
  /**
   * **A relationship word where a name should be** — `son`, `wife`, `guest`'s
   * kin, `my beautiful wife`, `Sarah's mother`. The vocabulary is
   * `isRoleName`'s in `server/src/modules/identity/first-name.ts`, which is the
   * resolver's own, so the import refuses exactly what the resolver refuses.
   *
   * Unlike the seven above it the plan does not turn this on: a word that names
   * nobody is not a judgement about a file. See the note on this list.
   */
  'role_name',
  /**
   * **Two rows of one file with one name and no identifier between them** (§1.5).
   * Whether they are one person listed twice or two people who share a name is
   * not a thing the file says, so it is a thing the office says: fix a name, set
   * one aside, or confirm the row as it stands and get two people.
   */
  'duplicate_name',
  /**
   * **Two rows that share one identifier and disagree on another** (§1.5). The
   * board's one member-number collision is this: two rows carry member `0001` and
   * two different monday item ids, so "same identifier, one person" and "same
   * name, different identifiers, two people" both apply and the file is wrong
   * about one of them. Folding either way would be a guess, so both rows wait.
   *
   * What they wait *for* is the office saying which reading is right, in the
   * same two words a repeated name is answered in — see
   * {@link ImportRowConflict}, and {@link ImportRowPreview.conflict} for the
   * pair and the identifier, which the sentence above names neither of.
   */
  'identifier_conflict',
  /**
   * **A row the profiler read as a channel rather than a person** (§1.17) — the
   * board's `zzz Cold Outreach`, which 75 rows point at as where they came from.
   *
   * The third of the reasons that is not a shape check the plan turns on, and it
   * is here for the reason the two identity outcomes are: it is a thing the
   * office has to be able to *see*. Until now a sentinel row was simply dropped
   * — excluded by the profile, skipped by the applier, never a row at all — and
   * the rule that finds it is a heuristic over how often a name is referenced.
   * A heuristic that removes people silently is a heuristic nobody can check,
   * and a real board had it wrong about a couple written on one line.
   *
   * So the row is kept, at `set_aside`, carrying this reason: it is on the
   * People tab, it is in the bin's counts, it writes nothing, and the one
   * gesture that was missing — "no, that is a person" — is the ordinary one of
   * giving it a level. References to it are still a record field on the row that
   * referred, exactly as before; that rule is about the *referrer* and does not
   * change with what the sentinel itself becomes.
   */
  'sentinel',
  'custom',
] as const;
export const BinRule = z.enum(BIN_RULES);
export type BinRule = z.infer<typeof BinRule>;

/**
 * The two of {@link BIN_RULES} that are **questions about identity** rather than
 * about the name on the row, and are therefore the two a name does not answer —
 * see the annotation above. Named here rather than spelled out at each reader so
 * that a third identity outcome joins one list and not three.
 */
export const IDENTITY_BIN_RULES = ['duplicate_name', 'identifier_conflict'] as const;
export type IdentityBinRule = (typeof IDENTITY_BIN_RULES)[number];

export const isIdentityBinRule = (rule: unknown): rule is IdentityBinRule =>
  typeof rule === 'string' && (IDENTITY_BIN_RULES as readonly string[]).includes(rule);

/**
 * **The two answers a person gives to `duplicate_name`** (§1.5) — the gesture
 * the bin was missing.
 *
 * The file says one name twice and does not say whether that is one person
 * listed twice or two people who share a name. Until now the office could only
 * answer sideways: set one row aside, which throws away everything it carried,
 * or give both a level, which keeps two people. Neither is the answer a bios
 * document actually wants, where the same entry is printed twice with an `&`
 * changed to an `and`.
 *
 *  - **`same`** — one person. The later rows **fold** onto the earliest row of
 *    the group: their prose, their facts, their relation requests and their
 *    identifiers all land on the survivor, and the verbatim repeat is dropped by
 *    the prose dedup rather than filed twice.
 *  - **`different`** — two people who share a name. Both rows keep their level
 *    and the reason is cleared, because it has been answered: the file was
 *    right, and re-running the dry run must not ask again.
 *
 * Stored on the resolution of **every row of the group**, not only the one the
 * gesture was made on: the question is about the group, and a row left holding
 * no answer is a row that still stops the commit.
 *
 * **The same two words answer `identifier_conflict`** (2026-09-15), because it
 * is the same question asked of a pair the file identifies rather than names —
 * see {@link ImportRowConflict}, which is where what each answer *does* to such
 * a row is written down. It is the only answer that clears one: a level says
 * what kind of thing the row is and leaves the file saying two rows are one
 * person.
 */
export const DUPLICATE_ANSWERS = ['same', 'different'] as const;
export const DuplicateAnswer = z.enum(DUPLICATE_ANSWERS);
export type DuplicateAnswer = z.infer<typeof DuplicateAnswer>;

/** One bin check the plan turns on. `custom` is the one that needs its detail. */
export const PlanBinRule = z
  .object({ rule: BinRule, detail: z.string().min(1).optional() })
  .strict();
export type PlanBinRule = z.infer<typeof PlanBinRule>;

/**
 * **How many corrections one plan may carry.** A file whose cells are wrong two
 * hundred times over is not a file with exceptions on it, it is a file that
 * needs fixing and uploading again — and the office is told exactly that rather
 * than being let build a plan nobody can read down.
 */
export const IMPORT_CORRECTIONS_MAX = 200;

/**
 * **How long a cell's text may be on either side of a correction.** It bounds
 * `from` and `value` together, because they are the same cell before and after
 * and a bound that let one outrun the other would be a pair the Plan tab could
 * not print on one line.
 */
export const IMPORT_CORRECTION_VALUE_MAX = 500;

/**
 * **`row_name` and `reason`**: a name and one sentence, the bound
 * {@link UNSUPPORTED_MEANING_MAX} already argues for — the office reads them in
 * a list beside the others, so a paragraph here is a paragraph nobody reads.
 */
export const IMPORT_CORRECTION_TEXT_MAX = 200;

/**
 * **`contacts:100` split into the sheet and the row**, or `null` where the
 * string is not that shape at all.
 *
 * On the **last** colon and only where what follows it is digits: a sheet may be
 * called `2026: Members`, and splitting on the first colon would look for a
 * sheet called `2026` and a row called ` Members`. A document's `p11b3` and a
 * couple's `p11b3#2` match nothing here, which is the answer a correction wants
 * — those name blocks, and corrections name a spreadsheet's cells.
 *
 * **Here rather than on the server**, where it was first written: the contract
 * has to decide whether two corrections name one cell, the office's plan editor
 * has to decide whether a correction's row is in the file, and the executor has
 * to find the row. Three readers of one address is three chances to read it
 * differently, and the first two of those three are the ones nobody would ever
 * see disagree.
 */
export const splitLocator = (locator: string): [sheet: string, row: number] | null => {
  const at = locator.lastIndexOf(':');
  if (at <= 0) return null;
  const row = locator.slice(at + 1);
  if (!/^\d+$/.test(row)) return null;
  return [locator.slice(0, at), Number(row)];
};

/**
 * **One spelling for one row** (2026-09-15 review) — `contacts:0100` and
 * `contacts:100` are the same cell, and this is the form both of them say.
 *
 * Every reader that *acts* on a locator already agrees about this, because every
 * one of them ends at `Number(row)`: the executor finds row 100 either way, and
 * so does the plan editor's range check. The reader that did not was the one
 * asking whether two corrections name the same cell — it keyed on the raw
 * string, so the pair the contract exists to refuse passed validation in two
 * spellings and then collided at apply, where the later of the two silently won
 * and the other sat on the Plan tab looking as applied as it is. That is the
 * exact silence {@link PlanCorrection}'s pair rule was written to remove.
 *
 * **Unchanged where it does not parse.** A locator this cannot read is one no
 * reader here can act on either, and an address nobody can read is better left
 * as the office's own words than rewritten into a shape it never had.
 */
export const normalizeLocator = (locator: string): string => {
  const at = splitLocator(locator);
  return at === null ? locator : `${at[0]}:${at[1]}`;
};

/**
 * **One cell of the file, read as something other than what it says** (§4.4).
 *
 * The case it exists for is Punta Brava's re-import: two rows carry member
 * number `0074` because the spreadsheet slipped, Aaron Graft's is `0075`, and
 * until now the office had nowhere to put that fact. Editing the file replaces
 * the bytes and drops every row answer with them (§4.2), and setting the row
 * aside loses the person. The plan is the document that says how the file is
 * read, and this is a sentence about how one cell of it is read, so it lives
 * here.
 *
 * It is the plan's **first row-level reference** — every other rule is keyed on
 * a column, a group title or a cell value — which is why it carries more than
 * the address it needs:
 *
 *  - `locator` is the row's address, `contacts:100`, the same spelling the
 *    executor's records use. Never a document's: corrections name a
 *    spreadsheet's cells, and a plan for a document that carries one is refused
 *    where the file's shape is known.
 *  - `column` is a {@link ColumnRef}, both halves, like every other reference in
 *    this contract — the index is what is read and the header is what is
 *    printed.
 *  - `from` and `row_name` are **not decoration**. They are the apply-time
 *    guard: a locator is a position, and a replaced file shifts every row below
 *    an inserted one, so the executor applies a correction only where the cell
 *    still says `from` and the plan's own name rule still reads `row_name` off
 *    that row. Anything else is an issue on the applied plan and nothing is
 *    written to the wrong person. They are also what the Plan tab prints —
 *    *Row 100, Aaron Graft · Member ID* — so the office reads the correction
 *    without holding the file open beside it.
 *  - `value` is **nullable and never optional**: "read that cell as empty" is a
 *    thing the office decided, and a key that could simply be missing would make
 *    a decision indistinguishable from a correction half-written. The empty
 *    string is refused (`min(1)`) so that emptiness has exactly one spelling.
 *  - `from` takes `''`, which is how an empty cell says what it says today; it
 *    is the file's word, trimmed, not a decision.
 *  - `reason` is the office's own sentence, and it is required because a
 *    correction nobody can account for a year from now is a number somebody
 *    typed.
 */
export const PlanCorrection = z
  .object({
    /** The row's address, `<sheet>:<row>` — never a document's. */
    locator: z.string().min(1),
    /** The name the file prints on that row, as the plan's name rule reads it. */
    row_name: z.string().min(1).max(IMPORT_CORRECTION_TEXT_MAX),
    /** Which column, by index and header, as every reference here is. */
    column: ColumnRef,
    /** What the cell says in the file, trimmed. `''` for an empty cell. */
    from: z.string().max(IMPORT_CORRECTION_VALUE_MAX),
    /** What to read it as. `null` is "read it as empty"; `''` is refused. */
    value: z.string().min(1).max(IMPORT_CORRECTION_VALUE_MAX).nullable(),
    /** The office's words, one sentence. */
    reason: z.string().min(1).max(IMPORT_CORRECTION_TEXT_MAX),
  })
  .strict();
export type PlanCorrection = z.infer<typeof PlanCorrection>;

/**
 * **What a correction changed, in the three words a person would say it in** —
 * `0074 → 0075`, `0074 → empty`, `empty → 0075`.
 *
 * Here rather than on either side because both sides say it: the Plan tab prints
 * this line under the row and the column it belongs to, and the executor's
 * record remark says the same change in a longer sentence. What must not differ
 * between them is **how an empty cell is spelled** — a blank where the value
 * should be reads as a rendering bug rather than as a decision, and the word for
 * it is a property of the contract, not a choice each renderer makes again.
 */
export function correctionSaid(correction: PlanCorrection): string {
  const said = (text: string | null) => (text === null || text === '' ? 'empty' : text);
  return `${said(correction.from)} → ${said(correction.value)}`;
}

/**
 * **The plan** — one file's whole mapping, proposed by the model, edited by the
 * office, executed by code that has no model in it.
 *
 * `occasion` is required and nullable rather than optional: "this file is not an
 * occasion" is a thing the planner decided (§1.3a), and a key that could simply
 * be missing would make a plan that never considered it indistinguishable from
 * one that did.
 *
 * `joins` is optional and merely absent on the files that have none, which is
 * most of them: unlike an occasion, a second sheet is not a question the planner
 * had to answer — it either saw one in the profile or there was not one.
 *
 * `record_field_rules` is the same sort of absence and defaults to the empty
 * list, so every reader downstream gets an array rather than re-deriving one.
 */
export const ImportPlan = z
  .object({
    version: z.literal(IMPORT_PLAN_VERSION),
    source: ImportPlanSource,
    identity: ImportPlanIdentity,
    resolution: ImportPlanResolution,
    status: ImportPlanStatus,
    occasion: ImportOccasion.nullable(),
    /**
     * **The file's rows are occasions, not people** — see {@link ImportRowEvent}
     * for the whole of what one row then produces.
     *
     * Defaulted to `null` rather than required-nullable, alone among the plan's
     * two "what is this file" keys, and the difference is not a change of mind:
     * `occasion` has been required since the contract was written, so every plan
     * ever stored answers it, and a plan that never considered it must stay
     * distinguishable from one that did. This key arrived afterwards. Every plan
     * in the version chain predates it and none of them is a row-event file, so
     * the honest reading of an absent key here is the answer `null` — and the
     * alternative, refusing to parse, would take every session written before
     * today off the Plan tab.
     */
    row_event: ImportRowEvent.nullable().default(null),
    columns: z.array(PlanColumn),
    /** Fields a group or a column sets on every row — see {@link PlanRecordFieldRule}. */
    record_field_rules: z.array(PlanRecordFieldRule).default([]),
    /**
     * **Cells the file gets wrong, corrected on the plan** — see
     * {@link PlanCorrection} for what one of them says and what guards it.
     *
     * Defaulted to `[]` the way `record_field_rules` is, and for the same two
     * reasons: every reader downstream gets an array rather than re-deriving
     * one, and — because a defaulted key parses a document that never carried it
     * — **{@link IMPORT_PLAN_VERSION} stays `1`**. Every plan stored before today
     * is a plan with no corrections on it, which is exactly what the default
     * reads it as, so nothing in the version chain has to be rewritten and no
     * session goes off the Plan tab to gain the key.
     *
     * Bounded at {@link IMPORT_CORRECTIONS_MAX}, and the refusal past it says
     * what to do instead rather than only that the plan is too long.
     */
    corrections: z
      .array(PlanCorrection)
      .max(IMPORT_CORRECTIONS_MAX, {
        message:
          'A plan can carry up to 200 corrections; past that, fix the file and upload it again.',
      })
      .default([]),
    /**
     * **The protection account every record this file writes belongs to**
     * (security fold, 2026-09-22) — required in a security org, refused in every
     * other, and the whole of what enabling mass import for that vertical costs.
     *
     * One account for the file rather than one per row, because that is how a
     * firm actually imports: a spreadsheet is the roster of one engagement. A
     * file spanning two accounts is two files, which is also the only reading
     * that keeps the wall a wall — a per-row account would put the choice in a
     * column a model mapped.
     *
     * Defaulted to `null` rather than required-nullable, so
     * {@link IMPORT_PLAN_VERSION} stays `1`: every plan stored before today is a
     * plan with no account on it, which is exactly what the default reads it as,
     * and a planner turn writes one without the model having to invent an id it
     * cannot know. The office names it on the Plan tab, and the commit refuses
     * without it.
     */
    account_id: Uuid.nullable().default(null),
    /** Secondary sheets whose rows attach to the primary's — see {@link ImportPlanJoin}. */
    joins: z.array(ImportPlanJoin).optional(),
    bin: z.array(PlanBinRule),
  })
  .strict()
  .superRefine((plan, ctx) => {
    unwritableFactTypes(plan.columns, ctx, ['columns']);
    (plan.joins ?? []).forEach((join, index) =>
      unwritableFactTypes(join.columns, ctx, ['joins', index, 'columns']),
    );
    oneCorrectionPerCell(plan.corrections, ctx);
  });
export type ImportPlan = z.infer<typeof ImportPlan>;

/**
 * **A fact type this plan names and could never write.**
 *
 * `ColumnTarget` already refuses a field the catalog does not have. This is the
 * mistake one level out: every field named is real, and the one the catalog
 * *requires* is not among them. `{type: 'employment', field: 'role'}` with no
 * column anywhere pointed at `org` describes an employment with no employer, and
 * the catalog's `employment.org` is not nullish — so every row of the file emits
 * a claim ingest refuses, and a refused claim costs the **whole row**, notes and
 * residences and memberships included (import note §1.8,
 * `import-commit.service.ts`). A plan is the right place to say it: once, on the
 * Plan tab, before a commit rather than 176 times after one.
 *
 * **Per column set, because that is what the executor builds a fact out of.** The
 * primary sheet's columns are collected in one pass and each join's in its own
 * (`apply-plan.ts`), so a join column holding `employment.role` does not find the
 * primary's `employment.org` — and a rule that read them together would call a
 * plan writable that writes nothing.
 *
 * The **mapped-but-empty** cell is a different question and not this one: a plan
 * can map both columns honestly and still meet a row where the employer cell is
 * blank. That is a per-row matter, and the executor drops the fact and says so on
 * the row.
 */
function unwritableFactTypes(
  columns: readonly PlanColumn[],
  ctx: z.RefinementCtx,
  path: readonly (string | number)[],
): void {
  /* One entry per fact type the set names, carrying the fields it maps and the
     column the issue is reported against — the *first* one of that type, which
     is the one the office would repoint or drop. Both are written on the same
     pass, so `firstAt` is never absent for a type in this map and there is no
     fallback below pretending otherwise. */
  const mapped = new Map<FactType, { fields: Set<string>; firstAt: number }>();
  columns.forEach((column, index) => {
    if (column.target.kind !== 'fact') return;
    const seen = mapped.get(column.target.type);
    if (seen) seen.fields.add(column.target.field);
    else mapped.set(column.target.type, { fields: new Set([column.target.field]), firstAt: index });
  });

  for (const [type, { fields, firstAt }] of mapped) {
    const missing = FACT_TYPE_REQUIRED_FIELDS[type].filter((field) => !fields.has(field));
    if (missing.length === 0) continue;
    const said = missing.map((field) => `'${field}'`).join(' and ');
    ctx.addIssue({
      code: 'custom',
      path: [...path, firstAt, 'target', 'type'],
      message:
        `${type} has no column mapped to ${said} — that type cannot be written without ` +
        `${missing.length === 1 ? 'it' : 'them'}, so map a column to ${said} or point this ` +
        `column somewhere else`,
    });
  }
}

/**
 * **Two corrections naming one cell, refused as a pair.**
 *
 * The plan is a document the office reads and edits, and two sentences about the
 * same cell are two answers to one question: whichever the executor happened to
 * apply last would win, and the other would sit on the Plan tab looking as
 * applied as it is. Neither losing in silence nor guessing which was meant is
 * something a contract can do, so the pair is refused and the office drops one.
 *
 * **The cell is the locator and the column's index**, not its header: the index
 * is what the executor reads, and §2.2's `updates` sheet carries two columns
 * spelled `Content Type`, so a check keyed on the words would refuse a legal
 * pair on one sheet and miss a real collision on another.
 *
 * **And the locator normalized** (2026-09-15 review), because the raw string is
 * the one thing here that has two spellings for one row: `contacts:0100` and
 * `contacts:100` both passed this check and then named the same cell at apply,
 * where the later of them won in silence — which is precisely the outcome the
 * pair rule exists to refuse. {@link normalizeLocator} is what every acting
 * reader already does to a locator, said once and asked of it here too.
 *
 * The issue is reported against the **later** of the two — the one the office
 * would drop — and names both by the position the issue's own path carries, so
 * the numbers in the sentence and the field the form highlights are the same
 * numbers.
 */
function oneCorrectionPerCell(corrections: readonly PlanCorrection[], ctx: z.RefinementCtx): void {
  const firstAt = new Map<string, number>();
  corrections.forEach((correction, index) => {
    const row = normalizeLocator(correction.locator);
    const cell = `${row}\u0000${correction.column.index}`;
    const earlier = firstAt.get(cell);
    if (earlier === undefined) {
      firstAt.set(cell, index);
      return;
    }
    ctx.addIssue({
      code: 'custom',
      path: ['corrections', index],
      /* The normalized address and not either correction's own spelling: where
         the two differ, that is the whole of what the office has to be told. */
      message:
        `corrections ${earlier} and ${index} both name row ${row}, ` +
        `column ${correction.column.index} — one cell takes one correction`,
    });
  });
}

/**
 * **Every column this plan can say the meaning of and cannot write** — the
 * primary sheet's and every join's, in the order the plan lists them.
 *
 * Here rather than on either side, because both sides count the same thing and
 * would have counted it differently: the server puts the number on
 * {@link ImportCounts.unsupported_columns} and the Plan tab renders the list,
 * and a tab whose heading said three over a list of two — because one side
 * forgot the joined sheet — is precisely the disagreement a count beside a list
 * must never have.
 */
export function unsupportedColumns(plan: ImportPlan): PlanColumn[] {
  return allPlanColumns(plan).filter((column) => column.target.kind === 'unsupported');
}

/** The primary sheet's columns, then every joined sheet's — all of them write the same people. */
export const allPlanColumns = (plan: ImportPlan): PlanColumn[] => [
  ...plan.columns,
  ...(plan.joins ?? []).flatMap((join) => join.columns),
];

/**
 * **Two columns writing one detail on the person**, reported against the later
 * of each pair.
 *
 * The executor keeps a record field by its key, so a second column with the
 * same key overwrites the first cell by cell and the plan loses a column without
 * saying so. The Plan tab's old default key, `field`, did exactly that to seven
 * columns of the Punta Brava contacts board (2026-09-29).
 *
 * Checked where a plan is **saved**, not in {@link ImportPlan}'s own refinement:
 * a stored plan is re-parsed on every read and a parse failure reads as "no
 * plan", so a schema rule would take the plan out from under a session already
 * committing one of these. The primary sheet and every join count together,
 * because they all write the same person's record.
 */
export function recordFieldKeyCollisions(
  plan: ImportPlan,
): { path: (string | number)[]; message: string }[] {
  const sets: { columns: readonly PlanColumn[]; path: (string | number)[] }[] = [
    { columns: plan.columns, path: ['columns'] },
    ...(plan.joins ?? []).map((join, index) => ({
      columns: join.columns,
      path: ['joins', index, 'columns'],
    })),
  ];
  const first = new Map<string, string>();
  const issues: { path: (string | number)[]; message: string }[] = [];
  for (const set of sets) {
    set.columns.forEach((column, index) => {
      if (column.target.kind !== 'record_field') return;
      const header = column.column.header || `column ${column.column.index}`;
      const earlier = first.get(column.target.key);
      if (earlier === undefined) {
        first.set(column.target.key, header);
        return;
      }
      issues.push({
        path: [...set.path, index, 'target', 'key'],
        message:
          `"${header}" and "${earlier}" both write the detail "${column.target.key}", ` +
          `so one would overwrite the other — give each its own name`,
      });
    });
  }
  return issues;
}

/* ————————————————————————— the session ————————————————————————— */

/**
 * **The states the office sees** (§1.1). `undone` is loud on purpose: a session
 * that was committed and then unwound is not the same thing as one that was
 * canceled before it wrote anything.
 */
export const IMPORT_STATES = ['in_progress', 'complete', 'canceled', 'undone'] as const;
export const ImportState = z.enum(IMPORT_STATES);
export type ImportState = z.infer<typeof ImportState>;

/**
 * **What the parser made of the file**: a grid (CSV, a workbook sheet) or a
 * document (the bios PDF). It is not the file type — {@link ImportFileRef}'s
 * `container` carries that — it is which of the two shapes chunk 1's parsers
 * produced, and therefore which record rule the plan can use.
 *
 * **Named `shape` rather than `container` (ruled in chunk 3).** The word was
 * `container` on both sides of this package until now, meaning two different
 * things: the profile's is the file — `csv | xlsx | pdf` — and this one is what
 * came out of reading it. The barrel could only re-export one of them, so the
 * other was reachable only by its constant list, and any reader holding an
 * `ImportContainer` had to know which of the two it had. One question, one word:
 * a **container** is what the bytes arrived in, a **shape** is what the parser
 * made of them.
 */
export const IMPORT_SHAPES = ['grid', 'document'] as const;
export const ImportShape = z.enum(IMPORT_SHAPES);
export type ImportShape = z.infer<typeof ImportShape>;

/**
 * **A foreign id, in the two halves the anchor stores it in** — the namespace is
 * ours (`monday:puntabrava`, `linkedin`) and the id is theirs.
 */
export const ImportExternalId = z
  .object({ namespace: z.string().min(1), id: z.string().min(1) })
  .strict();
export type ImportExternalId = z.infer<typeof ImportExternalId>;

/** The file itself, as the File tab and the ledger name it. */
export const ImportFileRef = z
  .object({
    filename: z.string().min(1),
    content_type: z.string().min(1),
    /**
     * **Which container the bytes arrived in** — `csv | xlsx | pdf`, the
     * profile's word and the same enum the profile carries. It sits beside
     * `content_type` rather than being derived from it because that header is
     * whatever the browser guessed (a `.csv` routinely arrives as
     * `application/vnd.ms-excel`), and which parser actually read the file is a
     * fact about the session rather than a guess to be re-made downstream.
     */
    container: ImportContainer,
    /** Bytes. The upload cap is 25 MB, under Cloud Run's 32. */
    size: z.number().int().nonnegative(),
    sha256: z.string().min(1),
  })
  .strict();
export type ImportFileRef = z.infer<typeof ImportFileRef>;

/**
 * **One upload the session has held**, with who put it there and when — the File
 * tab's history (§2.4).
 *
 * The file itself plus two facts about the act of uploading it. A session's
 * `file` is the *current* one and is a {@link ImportFileRef}; this list is every
 * one, oldest first, and the last of it is that same file. Replacing a file
 * before commit is an ordinary thing to do — a first export that turned out to
 * be the wrong sheet — and the office reading the session back needs to see that
 * it happened, because the plan was proposed against one of these and the rows
 * were read from another.
 */
export const ImportUpload = ImportFileRef.extend({
  uploaded_by: UserRef,
  at: Timestamp,
}).strict();
export type ImportUpload = z.infer<typeof ImportUpload>;

/**
 * **One column, as the Plan tab prints it beside the target** — the samples that
 * let a mapping be checked by eye.
 *
 * Five keys out of the profile's seventeen, and the choice is the whole point:
 * the stored `ColumnProfile` carries every distinct value with its count, the
 * ten most-referenced names, the bin counts and three examples per rule. All of
 * that is what the *planner* reads, and shipping it on a poll of a 71-column
 * board would be several hundred kilobytes per tick for a tab that renders one
 * line per column.
 *
 * `inferred_type` rather than `type`, which is what the profile calls it: on
 * this shape `type` would sit beside a `target.type` and a `kind` and read as
 * the column's role in the plan rather than as a guess about its cells.
 */
export const ImportColumnSummary = z
  .object({
    /** 1-based, the same address the plan's {@link ColumnRef} uses. */
    index: z.number().int().positive(),
    header: z.string(),
    /** Three, the profile's own sample count — enough to recognise a column by. */
    samples: z.array(z.string()),
    /** Share of data rows with something in this column, 0 to 1. */
    fill_rate: z.number().min(0).max(1),
    inferred_type: ColumnType,
    /**
     * **Cells of this column that name two people** (§1.16a) — what
     * `couple_headings` is to a document, said per column because a grid has no
     * one place for it: the count belongs to the column the plan reads names
     * from, and the Plan tab's glance cannot say how many people a `couple_cell`
     * plan produces without it. Zero on every column that holds none, which is
     * most of them.
     */
    couple_cells: z.number().int().nonnegative(),
    /**
     * **Cells of this column that name several organizations** (§1.16b) — what
     * the `split_orgs` transform reads, counted on every column before anything
     * knows what the column is for. Zero on every column that holds none, which
     * is most of them.
     */
    org_list_cells: z.number().int().nonnegative(),
  })
  .strict();
export type ImportColumnSummary = z.infer<typeof ImportColumnSummary>;

/** One sheet of the file: how much of it is people, and what its columns are. */
export const ImportSheetSummary = z
  .object({
    name: z.string(),
    /** Rows that stand for a person, after every exclusion. */
    data_row_count: z.number().int().nonnegative(),
    column_count: z.number().int().nonnegative(),
    /** What the file holds that is not a person, and how much of it. */
    exclusions: z.array(ProfileExclusion),
    /** The file's own classification: a monday board's group headings. */
    group_titles: z.array(GroupTitle),
    columns: z.array(ImportColumnSummary),
  })
  .strict();
export type ImportSheetSummary = z.infer<typeof ImportSheetSummary>;

/** A document's four numbers: entries, the ones naming nobody, couples, length. */
export const ImportDocumentSummary = z
  .object({
    heading_count: z.number().int().nonnegative(),
    non_name_headings: z.number().int().nonnegative(),
    /**
     * **Headings that name two people** — the couples the split rule is for, and
     * the difference between "146 entries" and "226 people". The Plan tab's
     * glance says how many people a document produces, and it cannot say it
     * without this: the arithmetic is one person per record heading plus one
     * more for every couple among them.
     */
    couple_headings: z.number().int().nonnegative(),
    /**
     * **The median words in a body block** — the one number that says whether the
     * blocks under those headings are bios or captions. The profile carries the
     * min, the median and the max; the tab prints one, and the middle is the one
     * that describes the file rather than its two outliers.
     */
    body_length: z.number().int().nonnegative(),
  })
  .strict();
export type ImportDocumentSummary = z.infer<typeof ImportDocumentSummary>;

/**
 * **What the file holds, small enough to poll** (§2.4) — the File tab's middle
 * section and the Plan tab's samples column.
 *
 * A summary and named as one: the whole profile is on the session's row and is
 * the planner's document, sized for a model reading it once. This read is polled
 * every three seconds while a session is moving, so what crosses is the part a
 * person looks at. The board — 2 sheets, 71 columns — is a few tens of
 * kilobytes here against most of a megabyte whole.
 */
export const ImportProfileSummary = z
  .object({
    /** One per sheet, in workbook order; empty for a document. */
    sheets: z.array(ImportSheetSummary),
    /** Present for a document, `null` for a grid. */
    document: ImportDocumentSummary.nullable(),
  })
  .strict();
export type ImportProfileSummary = z.infer<typeof ImportProfileSummary>;

/**
 * The counts the ledger and the People tab are made of. Two groupings over the
 * same rows — what each person will *be*, and what actually happened to them —
 * plus the one number the office acts on.
 */
export const ImportCounts = z
  .object({
    by_level: z
      .object({
        subject: z.number().int().nonnegative(),
        known: z.number().int().nonnegative(),
        set_aside: z.number().int().nonnegative(),
      })
      .strict(),
    by_outcome: z
      .object({
        pending: z.number().int().nonnegative(),
        created: z.number().int().nonnegative(),
        attached: z.number().int().nonnegative(),
        skipped: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
      })
      .strict(),
    /**
     * **What the dry run said would happen to each person**, one count per
     * outcome (§1.8) — the third grouping over the same rows, and the one the
     * office reads *before* it presses anything.
     *
     * `by_outcome` says what the commit did and is all `pending` until it has
     * run; this says what the commit *would* do, which is the question the
     * Import tab's opening sentence and the People tab's outcome chips are both
     * asking. Without it "25 already on record" could only be said after the
     * fact, which is precisely too late for it to be worth saying.
     *
     * Every key present and zero where nothing matched, unlike the two partial
     * records below: five outcomes is a fixed and very short list, and a chip
     * row that appeared and disappeared as answers landed would move under the
     * hand reaching for it. Zero on every row before the dry run has run.
     */
    by_preview_outcome: z
      .object({
        create: z.number().int().nonnegative(),
        attach: z.number().int().nonnegative(),
        ambiguous: z.number().int().nonnegative(),
        folded: z.number().int().nonnegative(),
        skip: z.number().int().nonnegative(),
      })
      .strict(),
    /**
     * **What each subject will be**, in the org's vertical's own words — the
     * summary line the Import tab opens with ("143 members, 252 prospects, 209
     * declined, 15 contacts"). A record rather than a fixed shape, because the
     * words are the vertical's and this package holds those as a loose union
     * (§0.7); a level that has no status contributes nothing to it.
     *
     * Added in chunk 3 and not in the note's §2.1 sketch, which names the
     * groupings the ledger needs. This is the one the *session page* needs, and
     * a client that had to count it out of the rows would be paging the People
     * tab to render a headline.
     *
     * **Keyed by {@link SubjectStatus} rather than by any string.** The words are
     * the vertical's, but they are all drawn from this package's own loose union,
     * and an open `z.record` let a status the contract has never heard of ride
     * onto the wire and into a client's `by_status[status]` lookup — where it
     * renders as a heading nothing else in the product spells. The record is
     * partial: a status no row carries is simply absent, which is what a club
     * with no declined prospects reads as.
     */
    by_status: z.partialRecord(SubjectStatus, z.number().int().nonnegative()),
    /**
     * **Why the rows in the bin are in it**, one count per rule that fired
     * (§1.8). The bin is one number the office acts on and several different
     * questions underneath it — five bad names and the two ends of an identifier
     * collision are not one gesture — so the People tab's chips are these counts
     * and `?bin_reason=` is what each chip asks the rows read for.
     *
     * **One row counts once, under the reason it is filed by.** A row can fail
     * several checks — an address that is also one word — and it carries all of
     * them on `ImportRowPreview.bin_reasons`; but the count is of *rows*, keyed
     * by `bin_reason`, which is the single field `?bin_reason=` filters on. The
     * two were different sums for a while and the chip said twelve over a list
     * of nine, which is the one thing a count beside a filter must never do.
     *
     * Partial by construction, exactly as the profile's own `bin_counts` is: only
     * the rules that actually fired are listed, and a clean file reads as `{}`.
     */
    by_bin_reason: z.partialRecord(BinRule, z.number().int().nonnegative()),
    /**
     * **Prose units the commit would file, and how many of them would be read**
     * (§1.11). Two numbers because they are two questions: a dated log of
     * fourteen entries is fourteen units, and the earlier tool's dossiers are
     * units that are kept and never extracted (§1.12). `to_read` is what the
     * price on the Import tab is a function of.
     *
     * **After the commit, `notes_to_read` is the reading's own total** — the
     * units this session actually filed and queued, and not what the plan said
     * it would file. The two differ, and the run strip reads "412 of 1,540" off
     * {@link notes_read} and this: `writeProse` files a unit once per person per
     * text, so a file with four near-duplicate entries files fewer than the plan
     * counted, and a total that stayed at the plan's number was a progress bar
     * that could never fill. Before the commit the queue is empty and the plan's
     * count is the only reading there is.
     *
     * The failures are **in** that total: a unit that would not read was filed
     * and queued and is finished, so leaving it out would make the denominator
     * fall while nothing was being read. `notes_read` stops short of this number
     * at the end instead, and {@link notes_failed} says by how many.
     */
    prose_units: z.number().int().nonnegative(),
    notes_to_read: z.number().int().nonnegative(),
    /**
     * **How far the commit has actually got** — the two receipts §1.9's
     * structural phase writes, on the wire so the run strip says "412 of 577"
     * rather than turning a spinner.
     *
     * `facts_written` is pass B's receipt: rows whose claims are in. It counts a
     * `skipped` row too, because a row written nowhere is a row that is finished,
     * and a progress number that never reached its total would be a progress
     * number nobody could read. `notes_read` is the reading phase's: prose units
     * this session filed whose extraction has come back (§1.11). Both are zero
     * on a session that has not committed.
     */
    facts_written: z.number().int().nonnegative(),
    /**
     * **Claims this session actually wrote** — the number that would have made a
     * bad commit visible, and the reason it is on the wire.
     *
     * `facts_written` counts *rows that pass B finished with*, and a row is
     * finished whether it wrote fourteen claims or none: a skipped row carries
     * that receipt too, by design, because it is a progress number against a row
     * total. So a session whose every claim ingest threw read as 618 of 618 with
     * nothing said about anybody, and there was nothing anywhere on the wire
     * disagreeing with it. This is the other question — what got written — and it
     * is counted off `claim.import_id` rather than off the rows, because that is
     * the thing being asked about.
     *
     * Zero before a commit, and zero after one on a file whose plan states no
     * facts at all: a bare contact list of names and emails writes identifiers,
     * not claims. It is the pairing that says something — rows finished with no
     * claims behind them.
     */
    claims_written: z.number().int().nonnegative(),
    notes_read: z.number().int().nonnegative(),
    /**
     * **Units the reading could not read** (§1.11) — a number the office has a
     * gesture for, and therefore one it has to be able to see.
     *
     * A unit fails for a reason of its own and its neighbours land: the extractor
     * refused it, or the person it belongs to was taken off the board between the
     * commit and the reading. The session then goes `complete` around it, which
     * is right — a failed unit is finished, not queued — and is exactly what made
     * the failures invisible: `notes_read` simply stopped short of
     * `notes_to_read` with nothing on the wire saying why, and the one door that
     * re-reads a unit (`POST /inputs/:id/extract-retry`) had nothing to hang off.
     *
     * Deliberately **not** a sibling of `by_outcome.failed`: that counts rows of
     * the file, and this counts units of prose. A row can land perfectly and
     * carry three notes, one of which will not read.
     */
    notes_failed: z.number().int().nonnegative(),
    /**
     * **The reading stopped at the money, and there is more of it** (§1.11).
     *
     * The money is the organization's monthly limit; an import session has no
     * spend ceiling of its own (run defaults sweep, 2026-09-26). The limit is
     * not a failure: the session stays `in_progress` on `reading_notes`, the
     * units nobody reached stay `pending` with their retry intact, and `error`
     * stays null. But a session sitting on `reading_notes` making no progress
     * looks identical to one whose leg is in flight, and the office would poll a
     * number that never moves. So the wire says which it is, and the Import tab
     * says to contact Arbiter, as every monthly-limit refusal does; pressing
     * "read the notes" again restarts it once the allowance moves.
     *
     * Derived, not stored: `phase = reading_notes` with units still pending in
     * an organization that has spent its month. False on every other session.
     */
    notes_paused_for_spend: z.boolean(),
    /** Bin rows and ambiguous rows together — "needs a look: 41" (§1.8). */
    needs_a_look: z.number().int().nonnegative(),
    /**
     * **Rows whose person landed and whose membership link did not** (§1.14).
     *
     * Separate from `by_outcome.failed` because it is not a failed row: the
     * person is imported, filed and on the People tab, and the one thing the
     * commit did not do is claim them as somebody's user of this system —
     * because another member already holds them, or because the person the
     * preview matched is no longer a live person to hold. Zero on almost every
     * session, which is the point: a number the office never sees except when
     * there is something to see.
     */
    links_skipped: z.number().int().nonnegative(),
    /**
     * **Rows the commit found a person for that the dry run had not** (§1.5) —
     * see {@link ImportCommitMatch}.
     *
     * These are inside `by_outcome.attached` and not beside it: they *are*
     * attachments, and the commit wrote them as such. What this counts is the
     * ones the office was shown as `create`, which is the pairing that says
     * something — the file was prepared against a database that has moved, and
     * the number of people it would have duplicated is this.
     *
     * Zero on almost every session, and on every one that has not committed.
     */
    matched_at_commit: z.number().int().nonnegative(),
    /**
     * **Columns the planner could describe and this build cannot write** — see
     * the `unsupported` arm of {@link ColumnTarget}, and
     * {@link unsupportedColumns}, which is what counts them.
     *
     * A count over the **plan**, alone among everything above it, which are all
     * counts over the rows. It is here anyway because it belongs to the same
     * question the rest answer — what is about to happen to this file — and the
     * pre-commit summary says it in the same list: "3 columns not importable
     * yet", beside "226 people" and "225 notes to read". A client that had to
     * walk the plan's columns and its joins' columns to render one line of that
     * list would be re-deriving a number the server already holds, and the two
     * would eventually disagree.
     *
     * **It never blocks a commit.** The office asked for the file to be brought
     * in; a column nobody can write yet is a thing to say, not a door to shut.
     *
     * Zero on a session with no plan, and zero on every plan whose planner had
     * a shape for every column it could name — which is most of them.
     */
    unsupported_columns: z.number().int().nonnegative(),
    /**
     * **What a file of occasions makes, in the three numbers it makes them in**
     * (owner's ruling, 2026-09-07) — see {@link ImportRowEvent}.
     *
     * They are counts over the *rows*, like everything above `unsupported_columns`
     * and unlike it, and they are three rather than one because they are three
     * different things and the office reads them as such: `events` is how many
     * occasions the file adds to the club's record, `participations` is how many
     * times somebody was at one, and `ties` is how many people this file says
     * know each other. A reservations year is a handful of hundreds, a couple of
     * hundred, and a couple of hundred, and no two of those numbers are
     * interchangeable.
     *
     * **`events` counts occasions and not rows.** Two bookings on one Tuesday
     * are one Tuesday — the event is deduplicated on its name and date within
     * the org and the session — so the number is what will be on the record
     * afterwards rather than how many times the file said it.
     *
     * **A folded person still participates.** The same member hosting ten
     * dinners is one person record and ten participations: the fold is about who
     * the person is, not about where they were, so the nine folded rows keep
     * their participation and their ties and the commit writes them under the
     * person the first row became. That is why these are summed off every row
     * rather than off the ones a commit will write a person from.
     *
     * All three are zero on every plan that is not a row-event one, which is
     * nearly all of them, and zero on a session with no plan.
     */
    events: z.number().int().nonnegative(),
    participations: z.number().int().nonnegative(),
    ties: z.number().int().nonnegative(),
  })
  .strict();
export type ImportCounts = z.infer<typeof ImportCounts>;

/**
 * **Where a session is inside `in_progress`** (§2.4).
 *
 * `state` says whether a session is alive; this says what it is doing while it
 * is. It was free text until 2026-09-06, on the argument that nothing branches
 * on it — and the two halves of the module then had to spell the same moment
 * identically in two places, which is a sentence that drifts on the first
 * rewording. So the wire carries the **key**, the planner and the executor both
 * write the key, and the office's words are one table beneath it.
 *
 * The order is the order a session moves through them, and each is a thing the
 * office can be looking at:
 *
 *  - `profiled` — the file is read and nobody has asked the planner anything;
 *  - `waiting_on_you` — the planner asked, and it is the office's move;
 *  - `plan_ready` — there is a mapping to look at;
 *  - `previewed` — the dry run is done and the import is the next click;
 *  - `importing` — rows are being written;
 *  - `reading_notes` — the notes stage is running behind the rows.
 *
 * `null` whenever the session is not moving at all.
 */
export const IMPORT_PHASES = [
  'profiled',
  'waiting_on_you',
  'plan_ready',
  'previewed',
  'importing',
  'reading_notes',
] as const;
export const ImportPhase = z.enum(IMPORT_PHASES);
export type ImportPhase = z.infer<typeof ImportPhase>;

/**
 * The office's own word for each phase — the run strip's grammar ("Reading
 * notes · 412 of 1,540"), which is what a person reads beside the session.
 *
 * Here rather than in the dashboard's `copy.ts` because the keys are the
 * contract and the two SPAs would otherwise each carry their own six.
 */
export const IMPORT_PHASE_LABELS: Readonly<Record<ImportPhase, string>> = Object.freeze({
  profiled: 'File read',
  waiting_on_you: 'Waiting on you',
  plan_ready: 'A plan to look at',
  previewed: 'Ready to import',
  importing: 'Importing',
  reading_notes: 'Reading notes',
});

export const IMPORT_MESSAGE_ROLES = ['user', 'agent'] as const;
export const ImportMessageRole = z.enum(IMPORT_MESSAGE_ROLES);
export type ImportMessageRole = z.infer<typeof ImportMessageRole>;

/**
 * **One answer the office can press**, and whether it is the one the plan was
 * already drawn with.
 *
 * `default` is what makes a question beside a plan a *proposal*: the plan
 * carries an answer already — the size rule's, or the one the description
 * stated — and the option that says so is drawn first and marked, so pressing
 * nothing is a legible decision rather than an unanswered question.
 *
 * **A bare string is still an option.** Every question written before this key
 * existed — and every stored transcript row holding one — is a list of plain
 * strings, and a session in the middle of a conversation must not stop
 * rendering because the shape grew a key. A string reads as an option with that
 * label and no default.
 */
export const ImportQuestionOption = z.union([
  z
    .string()
    .min(1)
    .transform((label): { label: string; default?: boolean } => ({ label })),
  z.object({ label: z.string().min(1), default: z.boolean().optional() }).strict(),
]);
export type ImportQuestionOption = z.infer<typeof ImportQuestionOption>;

/**
 * **One thing the planner needs settled before it can map the file** (§1.2) —
 * the shape of the import, what each person should become, a column it cannot
 * classify from its header and values.
 *
 * `id` is short and stable so an answer can name the question it answers.
 * `options` is there when there are a handful of sensible answers, so the office
 * picks rather than types; it is absent, not empty, when there are not.
 */
export const ImportQuestion = z
  .object({
    id: z.string().min(1),
    text: z.string().min(1),
    options: z.array(ImportQuestionOption).min(2).optional(),
  })
  .strict();
export type ImportQuestion = z.infer<typeof ImportQuestion>;

/**
 * Why one column got the target it got, in the planner's own words — what it
 * wants the office to look at first on the Plan tab. Written beside a proposal,
 * never instead of one.
 */
export const ImportPlanNote = z
  .object({ column: z.string().min(1), why: z.string().min(1) })
  .strict();
export type ImportPlanNote = z.infer<typeof ImportPlanNote>;

/**
 * **What the office settled, and which question it settles** — the answer half
 * of {@link ImportQuestion}.
 *
 * `question_id` is the `id` of the question being answered and `text` is the
 * answer in the office's own words, whether they picked an option or typed one.
 * Without the id the next turn shows the model a transcript of loose sentences
 * and the model re-asks a question it has already been answered, which in a
 * six-turn session is the whole of the cost.
 */
export const ImportAnswer = z
  .object({ question_id: z.string().min(1), text: z.string().min(1) })
  .strict();
export type ImportAnswer = z.infer<typeof ImportAnswer>;

/**
 * One turn of the session's chat. `plan_version` is the version this turn
 * produced, and `null` on a turn that only asked a question — a session's first
 * move is usually a question and no plan at all (§1.2).
 *
 * `questions` and `notes` are the structured half of what an agent turn said,
 * and they are the half the office *acts* on: the questions become the buttons
 * under the message, the notes become the columns flagged on the Plan tab.
 * `answers` is the same thing on a user turn — which of the standing questions
 * this message settled. All three are absent on a turn that carried none.
 *
 * **A turn carries both.** The opening turn of a session proposes the whole plan
 * *and* asks what each person from the file should become, on one message: the
 * size rule fills the plan's default in, and the question beside it is what makes
 * that a proposal rather than a decision the office never made. So `plan_version`
 * and `questions` on the same row is the ordinary case and not a contradiction —
 * the office may press import on the plan as it stands, or answer, which redraws
 * it as a new version.
 */
export const ImportMessage = z
  .object({
    seq: z.number().int().nonnegative(),
    role: ImportMessageRole,
    text: z.string(),
    plan_version: z.number().int().positive().nullable(),
    questions: z.array(ImportQuestion).optional(),
    notes: z.array(ImportPlanNote).optional(),
    answers: z.array(ImportAnswer).optional(),
    at: Timestamp,
  })
  .strict();
export type ImportMessage = z.infer<typeof ImportMessage>;

/**
 * Who wrote a version of the plan. `user` is the table editor's `PUT
 * /imports/:id/plan`, which reaches no model at all.
 *
 * **`retry` is version 1 of a session started from a finished one** (2026-09-14,
 * `POST /imports/:id/retry`). Nobody wrote it and no model proposed it: it is the
 * mapping the earlier session settled, copied forward so the office adjusts
 * rather than re-explains. Its own word rather than `user` or `agent`, because
 * both of those name a turn somebody took on *this* session and the Plan tab's
 * history would otherwise say a version was authored here that was not.
 */
export const PLAN_AUTHORS = ['agent', 'user', 'retry'] as const;
export const PlanAuthor = z.enum(PLAN_AUTHORS);
export type PlanAuthor = z.infer<typeof PlanAuthor>;

/** A version of the plan, kept whole — the session page shows its history. */
export const ImportPlanVersion = z
  .object({
    version: z.number().int().positive(),
    plan: ImportPlan,
    authored_by: PlanAuthor,
    at: Timestamp,
  })
  .strict();
export type ImportPlanVersion = z.infer<typeof ImportPlanVersion>;

/**
 * **One entry of the version chain, without the plan** — what the session read
 * carries so a reload can offer the history without shipping every mapping.
 *
 * `message_id` is the turn the version came out of, and `null` on a version the
 * office wrote in the table editor, which reaches no model and belongs to no
 * message. It is what lets the Plan tab put a version beside the sentence that
 * produced it rather than beside a timestamp.
 */
export const ImportPlanVersionSummary = z
  .object({
    version: z.number().int().positive(),
    authored_by: PlanAuthor,
    message_id: Uuid.nullable(),
    at: Timestamp,
  })
  .strict();
export type ImportPlanVersionSummary = z.infer<typeof ImportPlanVersionSummary>;

/* ————————————————————————— the undo's report ————————————————————————— */

/*
   Declared above the session rather than beside the door it answers, because
   `ImportSession.unwind_report` holds one: a const referenced before its own
   declaration is a module that throws on import, and the alternative — a
   `z.lazy` on a shape with no recursion in it — would be a workaround with
   nothing to work around.
*/

/**
 * **One person an unwind kept, and why** (§1.9, §4.1's *stop and report*).
 *
 * Unwind removes exactly what the session wrote — and stops at anybody who has
 * been worked on since. A note, a run, a status change, a fact the office
 * corrected: the person is left standing, listed here, and offered to the
 * existing shred. Removing them would delete somebody's later research along
 * with the file that was declared wrong.
 */
export const ImportKeptPerson = z
  .object({
    entity_id: Uuid,
    name: z.string(),
    /** In the office's words: "a note was added", "research has run". */
    reason: z.string().min(1),
  })
  .strict();
export type ImportKeptPerson = z.infer<typeof ImportKeptPerson>;

/** `POST /imports/:id/unwind`. */
export const UnwindImportResponse = z
  .object({
    undone: z.literal(true),
    unwind_decision_id: Uuid,
    removed: z
      .object({
        people: z.number().int().nonnegative(),
        anchors: z.number().int().nonnegative(),
        facts: z.number().int().nonnegative(),
        prose_inputs: z.number().int().nonnegative(),
        /**
         * Attendees taken off an event's graph by undoing a graph import (event
         * graph design §3). Zero on every import into the record; defaulted so
         * reports stored before the field existed still read.
         */
        attendees: z.number().int().nonnegative().default(0),
        /**
         * Attendees another upload still lists that had this upload's
         * identifiers or link taken back off them. Defaulted like `attendees`.
         */
        attendees_reverted: z.number().int().nonnegative().default(0),
      })
      .strict(),
    kept: z.array(ImportKeptPerson),
    /**
     * The attendee upload that replaced this one's list, when a replace undid
     * it rather than the office; null otherwise, and on reports stored before.
     */
    replaced_by: Uuid.nullable().default(null),
  })
  .strict();
export type UnwindImportResponse = z.infer<typeof UnwindImportResponse>;

/**
 * **A session, as `GET /imports/:id` answers it** — polled while it is in
 * progress.
 *
 * Money crosses in **dollars** here, unlike everywhere else in this package,
 * because what the reading has come to is read on the Import tab. The column
 * beneath it is micro-USD, the unit `ai_usage_event.cost_micro_usd` meters in,
 * and the conversion is the server's at this boundary.
 */
/**
 * **The reading stopped because the model is rate limiting, and this is when it
 * will be tried again** — `null` on every session that is not waiting.
 *
 * The third of the three ways a reading leg stops without failing, and the only
 * one nothing else on the wire can see. The monthly limit is derivable
 * ({@link ImportCounts.notes_paused_for_spend}); the leg's own clock hands on
 * immediately and is invisible by design. A quota is neither: the units stay
 * `pending`, `error` stays null, `phase` stays `reading_notes` — a session that
 * has stopped for eight minutes is byte-identical to one whose leg is mid-note,
 * and the office polling it watched a fraction that had stopped moving with
 * nothing anywhere saying why.
 *
 * **A moment rather than a boolean**, because the office's question is not "is
 * it waiting" but "for how long" — the difference between waiting two minutes
 * and waiting fifteen is the difference between carrying on watching and going
 * to do something else. The client counts down from it.
 *
 * **In the future, always.** The server projects the stored moment only while it
 * has not arrived; once it has, this is `null` again whether or not the
 * successor leg has actually started, because "we are waiting until a moment
 * that has passed" is not a sentence any surface can render honestly.
 *
 * It says nothing about whether the office must act. Unlike the monthly limit,
 * which waits on a person, this waits on a clock and offers nothing — which is
 * exactly why it has to be said out loud.
 */
const WaitingForModelUntil = Timestamp.nullable();

/* ————————————————————————— findings about the whole file ————————————————————————— */

/**
 * **The three things the dry run asks about the file rather than about a row**
 * (§4.3, 2026-09-14).
 *
 * Every gate before these answered a question about *one* row — does this name
 * look like a name, does this row need a look — and each of them was answered
 * correctly while a board imported 143 people under the title of their stage.
 * The question nobody asked is the one the office would ask in the first second
 * of looking at the People tab: why are they all called the same thing? These
 * are that question, in three shapes.
 *
 * - `names_repeat` — one name over a lot of rows, or very few names over all of
 *   them. The plan is probably reading the name from the wrong column.
 * - `names_not_names` — what the plan produced does not read like people's
 *   names, or reads like the file's own headings.
 * - `rows_write_nothing` — rows that would mint a person with a name and
 *   nothing else, because the plan mapped no other column they fill.
 *
 * A **row-event** plan (§1.3b) is exempt from the first two: its rows are
 * bookings, and thirty rows naming eight hosts is the file being right.
 */
export const IMPORT_FINDING_KINDS = [
  'names_repeat',
  'names_not_names',
  'rows_write_nothing',
] as const;
export const ImportFindingKind = z.enum(IMPORT_FINDING_KINDS);
export type ImportFindingKind = z.infer<typeof ImportFindingKind>;

/**
 * **One thing the dry run found about the whole file**, as the office reads it.
 *
 * `line` is one plain sentence written by the server, with the file's own
 * numbers in it, because the numbers are the whole of the argument: "143 of 176
 * rows share the name …" is a thing a person can check against the People tab,
 * and "this file looks wrong" is not. The client renders it as it arrives and
 * composes nothing of its own.
 *
 * `examples` is at most five strings quoted out of the file — the names, or the
 * rows — so the sentence can be believed without leaving the page. Text from a
 * file somebody else wrote, so a surface that renders it renders it as text.
 */
export const ImportFinding = z
  .object({
    kind: ImportFindingKind,
    /** The server's own sentence. Always said; never empty. */
    line: z.string().min(1),
    /** Up to five, and empty where the sentence needs none. */
    examples: z.array(z.string()),
  })
  .strict();
export type ImportFinding = z.infer<typeof ImportFinding>;

/** The event a graph import writes into, or null for an import into the record. */
export const ImportTargetEvent = z
  .object({
    entity_id: Uuid,
    display_name: z.string(),
  })
  .strict();
export type ImportTargetEvent = z.infer<typeof ImportTargetEvent>;

/**
 * **One attendee in a replace's dry run** (docs/notes/2026-09-30-replace-attendee-list-spec.md).
 * `node_id` is null for someone the new file adds. `standing` is the tier an
 * analysis gave them, in its own words ("Flagged", "To check"), when it raised
 * them (`raised`) or has them open (`open`); null otherwise. `detail` is what the group is about:
 * their seat, or "Table 8 → Table 12", or "employer Acme → Northwind".
 */
export const ImportReplacePerson = z
  .object({
    node_id: Uuid.nullable(),
    name: z.string(),
    standing: z
      .object({ label: z.string(), tone: z.enum(['raised', 'open']) })
      .strict()
      .nullable(),
    detail: z.string().nullable(),
    /** A detail the scan searches by changed: they go back to not scanned. */
    rescan: z.boolean(),
  })
  .strict();
export type ImportReplacePerson = z.infer<typeof ImportReplacePerson>;

/**
 * **What replacing the list does**, as the last dry run read it. Every group is
 * whole; the page shows five of each. The uploads replaced are the event's
 * complete uploads at the dry run, and the commit refuses if they changed.
 */
export const ImportReplaceDiff = z
  .object({
    replaces: z.array(Uuid),
    before: z.number().int().nonnegative(),
    after: z.number().int().nonnegative(),
    unchanged: z.number().int().nonnegative(),
    /** How many attendees the next scan will search: added plus sent back. */
    to_scan: z.number().int().nonnegative(),
    /** Not on the new file and on the records: they stay unless the office says. */
    on_record: z.array(ImportReplacePerson),
    /** Not on the new file, and an analysis raised them or has them open. */
    dropped_raised: z.array(ImportReplacePerson),
    dropped: z.array(ImportReplacePerson),
    added: z.array(ImportReplacePerson),
    seats: z.array(ImportReplacePerson),
    details: z.array(ImportReplacePerson),
  })
  .strict();
export type ImportReplaceDiff = z.infer<typeof ImportReplaceDiff>;

/**
 * **One finding the office has read**, as the press says so — the kind, and the
 * sentence they were actually looking at.
 *
 * **The sentence travels, and that is the whole of this type.** Confirming by
 * kind alone is confirming a *category*, and the category is stable while the
 * sentence is not: a dry run re-run between the tick and the press can turn "12
 * of 12 rows share the name" into "143 of 176 rows share the name" without
 * changing the kind, and a server checking kinds would take the old tick as an
 * answer about the new numbers. The office confirmed a sentence with this file's
 * counts in it; the sentence is what has to come back.
 *
 * The examples are not here. They are five rows quoted in support of the
 * sentence, they can change with a re-parse that leaves the sentence true, and
 * making a client echo them exactly would be a refusal waiting for a
 * whitespace difference.
 */
export const ConfirmedFinding = z
  .object({ kind: ImportFindingKind, line: z.string().min(1) })
  .strict();
export type ConfirmedFinding = z.infer<typeof ConfirmedFinding>;

export const ImportSession = z
  .object({
    id: Uuid,
    description: z.string(),
    file: ImportFileRef,
    /**
     * **Every upload this session has held, oldest first** — the File tab's
     * history (§2.4). The last entry is `file` above; a session nobody replaced
     * the file on has exactly one.
     *
     * On the session read rather than behind a door of its own because the tab
     * is drawn from the same read as the other three, and a history that arrived
     * late would be a section that appears under the reader.
     */
    files: z.array(ImportUpload),
    /** What the parser made of the file — see {@link ImportShape}. */
    shape: ImportShape,
    /**
     * **What the file holds** (§2.4) — the sheets, their columns with samples,
     * the rows left out and the group headings, or a document's three numbers.
     *
     * A summary of the stored profile and never the profile itself; see
     * {@link ImportProfileSummary} for the size argument. It is what the File
     * tab renders and what the Plan tab prints beside each target, so a mapping
     * can be checked against the cells it was written for without leaving the
     * page.
     */
    profile_summary: ImportProfileSummary,
    state: ImportState,
    phase: ImportPhase.nullable(),
    /**
     * **Whether this session has written anything** — `commit_decision_id` is
     * not null, which is the module's own definition of committed everywhere
     * else.
     *
     * On the wire because every surface that used to answer it from `phase` got
     * it wrong. `phase` says what a *moving* session is doing and is `null` the
     * moment it stops: a session that committed and finished, one that committed
     * and failed, and one that was never committed at all all read `phase:
     * null`. The Import tab keyed its whole shape on that and offered the read-
     * notes switch and the Import button again on a session that had already
     * run.
     *
     * `state` does not answer it either: `complete` and `undone` imply it, and
     * `in_progress` is where a session mid-commit lives.
     */
    committed: z.boolean(),
    /**
     * **When the dry run last read this session**, or `null` on one it has never
     * read — the same column the commit's own refusal is written against.
     *
     * The client's gate is the server's gate, said forwards: `null` here is
     * `IMPORT_NOT_PREVIEWED` waiting to happen, and the Import button says so
     * rather than being pressed to find out.
     */
    previewed_at: Timestamp.nullable(),
    /**
     * **Something has moved since that dry run** — the server's own staleness
     * rule, computed rather than re-derived by each client.
     *
     * Two things make a preview stale and only one of them is visible from
     * outside: a **plan** edit, which rebuilds the rows and leaves their stored
     * previews on an older version, and an **answer about a row** — a level, a
     * name fixed inline, a namesake picked — which changes who the row is and
     * leaves `preview.entity_id` pointing at the match the resolver made under
     * the old spelling. The second is the common one and there is nothing on the
     * wire a client could see it with, which is why this is a boolean the server
     * computes and not an arithmetic the dashboard repeats.
     *
     * `false` on a session that has never been previewed: nothing has gone
     * stale, there is simply no preview, and `previewed_at` is what says so.
     */
    preview_stale: z.boolean(),
    /**
     * **What stopped it**, on a session that failed rather than finished — the
     * column of the same name on the row. `phase` says what a moving session is
     * doing and is `null` the moment it stops, so without this a session that
     * died mid-commit would reach the office as a page with nothing on it saying
     * why.
     */
    error: z.string().nullable(),
    /**
     * The number of the plan the session is holding — the head of the version
     * chain, which the Plan tab reads and the office's next edit forks from.
     * `null` before the model has proposed one, which is where a session that is
     * still asking its first question sits (§1.2).
     *
     * **And `null` on a second occasion, which reads identically and is not the
     * same thing**: a version was written and this build's `ImportPlan` no longer
     * parses it. That is what the server answers "there is no plan" for, on
     * purpose — every door that executes a plan refuses without one, and a
     * half-understood mapping is the one thing a commit must not run off. The
     * chain beneath (`plan_versions`) still lists the version, because it
     * happened; a client that finds a version in the chain and no `plan_version`
     * is looking at exactly this, and the office's next edit writes a version
     * that does parse.
     */
    plan_version: z.number().int().positive().nullable(),
    /**
     * **The plan that number names**, whole — `null` wherever `plan_version` is.
     *
     * Here rather than only behind `GET /imports/:id/plan` because the session
     * page renders the Plan tab from the same read that renders everything else
     * on it: a reload that had to make a second call to know what the mapping
     * says is a page with a tab that arrives late, and the plan is one jsonb
     * column of a row this read has already loaded.
     */
    plan: ImportPlan.nullable(),
    /**
     * **The version chain, newest first** — what the Plan tab's history offers
     * and what an office reading a session back is looking for: which versions
     * there were, who wrote each, and which message it came out of.
     *
     * Summaries, not plans. The head is `plan` above; the older ones are a list
     * to *choose* from, and shipping every version of a two-hundred-column
     * mapping on every poll would be the largest thing on the wire for a list
     * nobody has clicked yet.
     */
    plan_versions: z.array(ImportPlanVersionSummary),
    /**
     * **The transcript, oldest first** (§1.2) — every turn of the session's
     * conversation with the planner, with the questions, the notes and the
     * answers each carried.
     *
     * On the session read because a session is rows in a table and nothing is
     * running between turns: the page is rebuilt from this on every reload, and
     * a reload that got the counts and the plan but not what was said would be
     * a chat window that empties itself when the office comes back to it.
     * Bounded by the transcript ceiling the planner refuses past
     * (`IMPORT_SESSION_TOO_LONG`), so this is a short list and not a feed.
     */
    messages: z.array(ImportMessage),
    counts: ImportCounts,
    /**
     * **What this session has actually spent reading**, in dollars — metered per
     * call on `ai_usage_event` and summed onto the session's own column.
     *
     * The only money on this contract, and deliberately: there was an
     * `estimated_usd` beside it, a unit count times a per-unit rate, and the rate
     * was out by an order of magnitude. A number nobody can stand behind is worse
     * than no number, so what is shown is what was spent — a fact, not a
     * forecast.
     */
    spend_usd: z.number().nonnegative(),
    extractor_tier: ExtractorTier,
    /**
     * **What the undo took back, and who it stopped at** — the answer
     * `POST /imports/:id/unwind` gave, kept.
     *
     * `null` on every session that has not been undone. It is stored rather than
     * only returned because the kept list is the whole point of the answer:
     * those people are still on the records, the office is being told which ones
     * and why, and a report that lived in a component's state was gone on the
     * next reload — the one moment somebody comes back to check the list they
     * were shown.
     */
    unwind_report: UnwindImportResponse.nullable(),
    created_by: UserRef,
    created_at: Timestamp,
    updated_at: Timestamp,
    /**
     * **When this session last actually moved**, which is not the same question
     * as `updated_at` and is the one the strip's "no signal for Nm" is asking.
     *
     * `updated_at` moves when the session *row* is written, and a leg writes
     * that row about once a batch; between two of those it is minting people,
     * landing rows' facts and reading notes into four other tables. So the page
     * counted silence from the last batch boundary while the counts under it
     * moved — a working import called stuck, which for a job that runs for hours
     * is the one thing the line must never say.
     *
     * `null` on a session that has not done a unit of work yet, and on every
     * session that finished before the column existed. The page falls back to
     * `updated_at` there, which is what it read before.
     */
    progress_at: Timestamp.nullable(),
    waiting_for_model_until: WaitingForModelUntil,
    /**
     * **The finished session this one was started from** (2026-09-14, `POST
     * /imports/:id/retry`), or `null` on a session somebody uploaded a file to.
     *
     * A retry is a fresh session on the *same stored bytes*, carrying the plan
     * the earlier one settled and the answers already given about its rows. The
     * two are separate units of undo and separate transcripts — nothing about
     * the earlier one moves — so this is the only thread between them, and the
     * page spends it on one line back to where the work came from.
     *
     * `.nullable()` and not `.optional()`: every session answers this question,
     * and most of them answer "nothing" (the standing rule for a response field
     * that can be absent).
     */
    retried_from: Uuid.nullable(),
    /**
     * **The event whose attendee graph this session imports into**, or null for
     * an import into the record (event graph design §3). A graph import writes
     * nodes, never people or facts.
     */
    target_event: ImportTargetEvent.nullable(),
    /** This attendee list replaces the event's list rather than adding to it. */
    replaces_list: z.boolean(),
    /**
     * What replacing the list does, as the last dry run read it; null until a
     * dry run of the plan the session holds, and on every other import.
     */
    replace_diff: ImportReplaceDiff.nullable(),
    /**
     * **What the last dry run found about the file as a whole** (§4.3), and an
     * empty list on every session it found nothing on — which is nearly all of
     * them.
     *
     * An array and not `.nullable()`, because an empty list is the honest
     * "none" here: every previewed session answers this question, and a session
     * that has never been previewed has found nothing, which is the same list.
     *
     * **Stored per dry run, and dropped the moment the plan moves.** The
     * findings are about a plan applied to these bytes, so a list taken against
     * version 3 says nothing about version 4; the server publishes the stored
     * list only while its plan version is the one the session is holding, and
     * `[]` otherwise. It is the page's copy and the planner's, not the commit's:
     * the commit recomputes from the records it has in hand and refuses with
     * `IMPORT_HALTED` unless `CommitImportBody.confirm_findings` names every
     * kind it finds.
     */
    findings: z.array(ImportFinding),
    /**
     * **What the last dry run could not do with the plan itself** — one plain
     * sentence per problem, and an empty list on nearly every session.
     *
     * Today it is the corrections and nothing else (§4.4): *Row 100, Aaron
     * Graft, "Member ID": not applied; the cell says 0081, not 0074.* The
     * executor has always had a place to put a problem with the plan rather than
     * with a row (`AppliedPlan.issues`), and until now **nothing published it**:
     * the plan naming a sheet the file does not have was a sentence built, put
     * on an object, and dropped on the way to the wire. A correction that did
     * not apply is the first of those the office has to be able to see, because
     * it is the one they asked for by hand — so the channel is opened here and
     * every plan-level problem comes down it, not only the corrections'.
     *
     * An array and never `.nullable()`, for the reason {@link findings} is one:
     * every previewed session answers this, and a session nobody has dry-run has
     * found nothing, which is the same list.
     *
     * **Stored per dry run and dropped the moment the plan moves**, exactly as
     * the findings are and through the same rule — an issue is a statement about
     * a plan applied to these bytes, and one taken against version 3 says
     * nothing about version 4. It is the page's copy; the commit recomputes,
     * and refuses with `IMPORT_CORRECTION_UNAPPLIED` where a correction is among
     * them.
     */
    plan_issues: z.array(z.string().min(1)),
  })
  .strict();
export type ImportSession = z.infer<typeof ImportSession>;

/**
 * One row of the Imports ledger (§2.4) — description, file, who, when, state, and
 * the counts that matter for its state. The file is its name only: the ledger
 * lists sessions, and the rest of the file's story is on the session page.
 */
export const ImportSessionListEntry = z
  .object({
    id: Uuid,
    description: z.string(),
    filename: z.string().min(1),
    state: ImportState,
    phase: ImportPhase.nullable(),
    counts: ImportCounts,
    /**
     * Here for the reason the pause is derivable and this is not: the ledger's
     * state cell says which rows are stopped, and a session waiting out a rate
     * limit reads `in_progress` on `reading_notes` — indistinguishable, from
     * this row alone, from one that is reading. See {@link WaitingForModelUntil}.
     */
    waiting_for_model_until: WaitingForModelUntil,
    /** The event an attendee upload goes into, as on the session; null otherwise. */
    target_event: ImportTargetEvent.nullable(),
    created_by: UserRef,
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .strict();
export type ImportSessionListEntry = z.infer<typeof ImportSessionListEntry>;

/**
 * **The disagreement a row is being held for, named** (§1.5, 2026-09-15).
 *
 * `identifier_conflict` is the one bin reason that is a statement about a *pair*
 * and was rendered as a statement about a row: "two rows share an identifier and
 * disagree on another", on two rows, saying neither which identifier nor which
 * rows. On Punta Brava's re-import that was Aaron Graft and Kyle Allen, four
 * hundred rows apart, both carrying member number `0074` because somebody typed
 * it twice — and the office could not see either half of the sentence.
 *
 * So the dry run says what it already knew:
 *
 *  - `rows` — **every row of the disagreement, this one among them**, in the
 *    file's own order, exactly as {@link ImportRowPreview.duplicate_group} is
 *    the whole group and for the same reason: the tab draws the question from
 *    whichever end of it is on the page. The first entry is the survivor a fold
 *    lands on, which is what *same person* below means.
 *  - `shared` — **the identifiers more than one of those rows claims**, said the
 *    way the dry run's own lines say them ("member number 0074"). Empty on the
 *    one shape where nothing in the *file* is shared: two rows whose different
 *    identifiers both resolve to one person already on record.
 *  - `disagree_on` — the identifier kinds on which two of the rows carry
 *    different values, which is the other half of why the file cannot be
 *    believed.
 *
 * **The two answers are {@link DuplicateAnswer}'s**, and they are not the two
 * `duplicate_name` gets:
 *
 *  - `same` — one person, so the later rows fold onto `rows[0]` and that person
 *    ends up holding every identifier of the group, the disagreeing ones
 *    included. It is the answer for a row printed twice.
 *  - `different` — two people, so both rows are kept **and the shared
 *    identifiers are written to neither**. A member number two rows claim is not
 *    evidence for either of them, and writing it to both is what put `0074` on
 *    two people on prod. Each row says so in its own dry-run lines.
 *
 * **And the set answers with one voice or not at all** — see `answer` below,
 * which is the field every consequence is keyed on.
 */
export const ImportRowConflict = z
  .object({
    rows: z
      .array(
        z
          .object({
            locator: z.string().min(1),
            display_name: z.string(),
            source_label: z.string(),
          })
          .strict(),
      )
      .min(2),
    shared: z.array(
      z
        .object({
          kind: IdentifierAnchorKind,
          /** "member number 0074", "email agraft@example.org". */
          summary: z.string().min(1),
        })
        .strict(),
    ),
    disagree_on: z.array(IdentifierAnchorKind),
    /**
     * **What the set as a whole has been answered**, or `null` while it has not
     * been — and `null` is what a set whose rows say *different* things reads,
     * not one of the two.
     *
     * The answer is a statement about the set, so unanimity is what makes it
     * one. A set can hold two answers without anybody having done anything
     * careless: a correction re-unions two sets that were separately settled, a
     * plan edit brings a third row into one, two tabs answer at once. Reading
     * the first answer in the file's order and acting on it would then fold rows
     * somebody had just called different people, or write an identifier off the
     * back of an answer half the set does not hold. So a mixed set is an
     * unanswered set: nothing folds, nothing is dropped, and the rows are held
     * until they agree.
     *
     * It is this field and not the rows' own `duplicate` that every consequence
     * is keyed on — the bin (`needsALook`), the fold, and the commit's drop —
     * while {@link ImportRowResolution.duplicate} stays what it was: the input,
     * one per row, which the answer doors write across the whole set.
     *
     * **An answer is an answer to a particular set**, which is why
     * {@link ImportRowResolution.duplicate_for} exists: a correction that merges
     * two sets the office settled separately leaves every row holding a word it
     * said about a question nobody asked. Only answers stamped with *this* set
     * count here; the rest read as no answer at all.
     */
    answer: DuplicateAnswer.nullable(),
    /**
     * **Every row has answered, and they do not agree** — as opposed to a set
     * somebody has simply not got to yet, which reads `answer: null` too.
     *
     * The two look identical from one row, and only the server can tell them
     * apart: it is the one reading that has to be made about the whole set. The
     * People tab says so in a line, because a pressed chip on a row that is still
     * in the bin is otherwise a page with nothing on it that explains itself.
     */
    mixed: z.boolean(),
  })
  .strict();
export type ImportRowConflict = z.infer<typeof ImportRowConflict>;

/**
 * **What the dry run says will happen to this person** (§1.8): match by confirmed
 * anchor, then exact normalized name, then nothing.
 *
 *  - `create` — nobody by this identity is on record;
 *  - `attach` — they are, so facts and prose are added and the status is left
 *    alone unless the plan says otherwise;
 *  - `ambiguous` — several live namesakes, and the office picks;
 *  - `folded` — this person arrived earlier on the same file: the same
 *    identifier twice, or a repeated name the office answered "same person"
 *    ({@link DuplicateAnswer}). Everything the row carries goes onto the row it
 *    folds onto, which `fold_target` names;
 *  - `skip` — an exclusion or a set-aside.
 */
export const IMPORT_PREVIEW_OUTCOMES = ['create', 'attach', 'ambiguous', 'folded', 'skip'] as const;
export const ImportPreviewOutcome = z.enum(IMPORT_PREVIEW_OUTCOMES);
export type ImportPreviewOutcome = z.infer<typeof ImportPreviewOutcome>;

/** What actually happened at commit. `pending` is every row before one. */
export const IMPORT_ROW_OUTCOMES = ['pending', 'created', 'attached', 'skipped', 'failed'] as const;
export const ImportRowOutcome = z.enum(IMPORT_ROW_OUTCOMES);
export type ImportRowOutcome = z.infer<typeof ImportRowOutcome>;

/**
 * The preview for one row.
 *
 * `candidates` is {@link NameConflictCandidate}, the shape the create door's
 * namesake question already answers with, because it is the same question asked
 * from a different door and the office answers it with the same pick list and
 * candidate card. `bin_reason` is the deterministic shape check that put the row
 * in the needs-a-look list; `attachments` are the lines the People tab shows for
 * confirmation when the commit would add something to a person **already on
 * record** (§1.3a).
 */
export const ImportRowPreview = z
  .object({
    outcome: ImportPreviewOutcome,
    /** Conflicting owners must be corrected; picking a person cannot transfer an identifier. */
    identity_conflict: z.boolean().optional(),
    relationships: z
      .array(
        z
          .object({
            target_name: z.string(),
            key: z.string(),
            selected: z.string().nullable(),
            candidates: z.array(
              z
                .object({
                  locator: z.string(),
                  display_name: z.string(),
                  source_label: z.string(),
                  identifiers: z.array(z.string()),
                })
                .strict(),
            ),
          })
          .strict(),
      )
      .optional(),
    candidates: z.array(NameConflictCandidate).nullable(),
    bin_reason: BinRule.nullable(),
    /**
     * **Every rule that caught this row**, primary first — the one line the row
     * reads plus the ones underneath it ("also: unsplit couple").
     *
     * `bin_reason` is the *recorded* reason: the one the row is filed under, the
     * one `?bin_reason=` filters on and the one `counts.by_bin_reason` counts, so
     * a chip's number and the list behind it are the same set. A row can fail
     * several checks at once — an address that is also one word, a name a shape
     * check caught that is also one end of an identifier collision — and before
     * this the extra ones were simply lost, which made the row's single line an
     * incomplete account of why it is where it is.
     *
     * Empty on a row nothing caught, and never merely absent: an array that is
     * sometimes a key is an array every reader has to defend against.
     */
    bin_reasons: z.array(BinRule),
    /**
     * **The proposed facts summary** (§2.1) — one human-readable line per thing
     * this row will write: "employment · Bain, Partner", "member number 0142",
     * "3 notes". It is what makes the dry run a thing the office can *read*
     * rather than a count of rows, and it is the sibling of `attachments`: the
     * same sort of line, for a person being created rather than one already on
     * record. `null` on a row that writes nothing — a `skip`, a `folded` —
     * spelled the way its three siblings spell absence rather than as a key that
     * is simply not there.
     */
    proposed: z.array(z.string().min(1)).nullable(),
    attachments: z.array(z.string().min(1)).nullable(),
    /**
     * **The row this one folds onto** — the earlier row's locator, and the whole
     * of what `folded` means for the office: "the same person arrived earlier on
     * this file, and everything on this row goes onto that one".
     *
     * On the wire because a fold is now a thing the office *asks for* and not
     * only a thing the resolver notices: a `duplicate_name` row answered "same
     * person" folds onto the earliest row of its group, and a line reading
     * "folded" with nothing after it would leave the office to work out which of
     * the two rows survived. `null` on every row that is not folded.
     */
    fold_target: z.string().min(1).nullable(),
    /**
     * **The prose this row will file, and whether the import reads it** (§1.11,
     * §1.12) — the dry run's answer to "what happens to the bio in this entry".
     *
     * `units` is how many prose units the commit would write for this row: one
     * per note cell, one per entry of a dated log the plan splits. `read_at_import`
     * is whether the plan has the extractor read them — a note is read, a pasted
     * dossier is kept as background and never extracted, and a plan committed
     * with `read_notes: false` reads nothing at all.
     *
     * Two fields rather than a count of each, because the People tab's sentence
     * is "1 bio kept as a note, read after import": the number is what the row
     * files and the flag is what becomes of it. The row-level pair of
     * {@link ImportCounts.prose_units} and {@link ImportCounts.notes_to_read},
     * which are the same two questions asked of the whole session.
     *
     * `units: 0` on a row with no prose at all, and on a folded row, whose units
     * are counted on the row it folds onto.
     */
    notes: z
      .object({
        units: z.number().int().nonnegative(),
        read_at_import: z.boolean(),
      })
      .strict(),
    /**
     * **The opening of what this row actually says** — the first ~240 characters
     * of its first prose unit, whitespace flattened: the board's first note, the
     * PDF entry's opening line. `null` on a row that carries no prose at all.
     *
     * It is on the wire for the repeats (§1.5). Two rows reading `Preman Pendton
     * · duplicate name` are the same question twice, and the only thing that
     * tells the office whether they are one person is what each entry *says* —
     * so the text has to be on the row rather than a click away on a screen the
     * session has not written yet.
     */
    excerpt: z.string().nullable(),
    /**
     * **Where in the file this row came from, as a person would say it** — "row
     * 42", "row 42 of updates", "page 11".
     *
     * Derived from the locator, which is the parser's own address and reads like
     * one (`csv:42`, `p11b3`). The locator stays the key every gesture is made
     * against; this is the thing a person reads beside two copies of one name to
     * tell them apart, and it is never announced as an internal key.
     */
    source_label: z.string(),
    /**
     * **The rows of this file carrying this name, this one among them** (§1.5),
     * in the file's own order — what the office is being asked about on a
     * `duplicate_name` row, and therefore what the row has to be able to show.
     *
     * The whole group rather than the rest of it, so the tab can draw the group
     * from **any** member it happens to have on the page: the copies of one name
     * are one card with one question on it, and a card that could only be built
     * from the survivor would fall apart on the page that begins after them. The
     * first entry is the survivor a fold lands on, which is what
     * {@link ImportRow.group_key} names.
     *
     * Each entry carries what its line of the card renders — where the copy is
     * and what it says — because the copy itself may be on another page.
     *
     * `null` on every row that is not a `duplicate_name` one, which is all but a
     * handful of them.
     */
    duplicate_group: z
      .array(
        z
          .object({
            locator: z.string().min(1),
            display_name: z.string(),
            source_label: z.string(),
            excerpt: z.string().nullable(),
          })
          .strict(),
      )
      .nullable(),
    /**
     * **Which rows disagree with this one, and about what** (§1.5) — `null` on
     * every row that is not an `identifier_conflict` one.
     *
     * See {@link ImportRowConflict}. The reason alone said "two rows share an
     * identifier and disagree on another" and named neither the identifier nor
     * the other row, which is a sentence nobody can act on: Punta Brava's
     * re-import put it on two rows four hundred apart, and the office had to go
     * looking for the pair the dry run already knew.
     */
    conflict: ImportRowConflict.nullable(),
    /**
     * **What this person was to the occasions on this file** (owner's ruling,
     * 2026-09-07) — `null` on every row of every plan that is not a row-event
     * one, which is nearly all of them.
     *
     * The People tab's line for such a row is "host · 12 occasions" or "guest of
     * Ada Wren · 2 occasions", and all three parts of that are here rather than
     * derivable on the client: `occasions` counts the rows folded onto this one
     * as well as this one, which is a fact about the whole file and not about
     * the row the tab happens to be drawing, and `host` is a name off *another*
     * row, which the page may never hold.
     *
     * `host` is null on a host's own row, on a row whose plan states no host
     * column, and on one whose host cell was empty — a member dining alone.
     */
    row_event: z
      .object({
        role: RowEventRole,
        /** Occasions this person is on, this row and every row folded onto it. */
        occasions: z.number().int().positive(),
        /** Whose guest, where the row says and this person is not the host. */
        host: z.string().min(1).nullable(),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type ImportRowPreview = z.infer<typeof ImportRowPreview>;

/**
 * **The person the commit found for a row the dry run said to create** (§1.5,
 * §1.9) — the receipt for the one identity decision that is *not* made at preview
 * time.
 *
 * The dry run reads the database as it stands and writes `create` on a row it
 * finds nobody for. Between that read and the commit's write sits however long
 * the office spent on the plan, and a second import session prepared in that
 * window is previewed against the same empty answer: both say `create`, both
 * mint, and the club has two of everybody. So the commit asks again, per row,
 * inside the name lock it already holds — the record's own identifiers as
 * confirmed anchors, then the exact normalized name — and where exactly one live
 * person comes back it **attaches** rather than minting.
 *
 * This is what says so. Present only on those rows, so its absence is the
 * ordinary case; `by` says which of the two reads found the person, because
 * "your file's member number is already on record" and "somebody of this name is
 * already on record" are different things to be told. The row's own `outcome` is
 * `attached` either way, and {@link ImportCounts.matched_at_commit} counts them.
 *
 * **Not a preview outcome.** `preview.outcome` is left exactly as the dry run
 * wrote it — it is the statement the office approved, and rewriting it after the
 * fact would make the approved numbers unreadable. What actually happened is the
 * row's outcome and this.
 */
export const ImportCommitMatch = z
  .object({
    /** The live person this row landed on instead of a new one. */
    entity_id: Uuid,
    /**
     * Which read found them: a confirmed anchor on one of the record's own
     * identifiers, or the exact normalized name with nothing else to go on.
     */
    by: z.enum(['identifier', 'name']),
  })
  .strict();
export type ImportCommitMatch = z.infer<typeof ImportCommitMatch>;

/**
 * **What the office decided about this row**, and who decided it — the receipt
 * behind a level or a name that is not what the plan proposed. Absent on a row
 * nobody has touched, which is most of them.
 *
 * **`set_by_user_id` and `at` are the office gesture's own two fields**, and they
 * are optional for one reason: {@link matched_at_commit} is written by the
 * commit and not by a person, so a row that carries only that carries neither.
 * They are present on every key above — the server stamps them on every answer
 * door — and the staleness rule reads `at` to say that an answer postdates the
 * dry run, which is a thing the commit's own note must not claim to be.
 */
export const ImportRowResolution = z
  .object({
    level: ResolutionLevel.optional(),
    status: SubjectStatus.optional(),
    /** A name fixed inline, replacing what the file said. */
    name: z.string().min(1).optional(),
    /** The candidate chosen for an ambiguous row. */
    pick: Uuid.optional(),
    /** Named relationship target -> selected row in this same import. */
    relation_picks: z.record(z.string().min(1), z.string().min(1)).optional(),
    /**
     * The answer to "is this the same person as the other row?" — see
     * {@link DuplicateAnswer}. It answers both questions that ask it: the
     * repeats of one name, and the rows of one `identifier_conflict`, where
     * `different` is also what tells the commit to write the shared identifier
     * to neither of them ({@link ImportRowConflict}).
     */
    duplicate: DuplicateAnswer.optional(),
    /**
     * **Which set {@link duplicate} was an answer to**, on an
     * `identifier_conflict` row — a fingerprint of that set's member locators as
     * the dry run had them when the office pressed the chip. Absent on a
     * `duplicate_name` row, whose question is a name and not a set, and on every
     * row nobody has answered.
     *
     * It exists because a set is not a fixed thing. A correction to one cell can
     * merge two disagreements the office settled separately, a plan edit can
     * bring a third row into one, and every row then holds a word it said about
     * a question nobody asked. Unanimity that counted those words would fold rows
     * on the strength of an answer given about a different pair. So an answer
     * carries the question it answered, and a set is settled only where every
     * member's answer names *this* set.
     *
     * **The server writes it and no client may send it.** The answer doors
     * ({@link SetRowBody}, {@link BulkRowsSet}) are `.strict()` and do not carry
     * it, which is what keeps it a record of what was on the screen rather than
     * an assertion a caller can make.
     *
     * Optional rather than nullable, unlike a response field that can be "none":
     * every key of this bag spells absence by being absent, `duplicate` itself
     * included, and one key spelling it differently is one more thing every
     * reader has to know.
     */
    duplicate_for: z.string().min(1).optional(),
    /** The commit's own late answer to who this row is — see {@link ImportCommitMatch}. */
    matched_at_commit: ImportCommitMatch.optional(),
    set_by_user_id: Uuid.optional(),
    at: Timestamp.optional(),
  })
  .strict();
export type ImportRowResolution = z.infer<typeof ImportRowResolution>;

/**
 * **The membership link this row's commit did not write, and why** (§1.14).
 *
 * A row whose record matches a member of this system carries the link the commit
 * would set — this person *is* that user. One person can be at most one member's
 * record, so a link the office set by hand beforehand wins: it was somebody
 * saying so, and the file merely resolved a name. The row itself still lands, in
 * full; the link alone is skipped, and this is the receipt for it.
 *
 * `user_id` is **the member who already holds the person**, so the People tab can
 * say who to go and ask, and is `null` on the other reason there is — the person
 * the preview matched is no longer a live person of this org to link at all.
 *
 * **The two refusals `user_id` distinguishes, spelled out**, because the null is
 * doing real work and the sentence beside it is prose:
 *
 *  - **non-null** — the write was refused by the *link itself*: another member of
 *    this organization is already recorded as this person, and one person is at
 *    most one member's record. Their id is here so the tab can name them; the
 *    standing link wins because somebody set it by hand, where the file merely
 *    resolved a name.
 *  - **null** — the write was refused by the *person*: they are no longer a live
 *    person of this organization (tombstoned, merged down a broken chain, of
 *    another org, or not a person at all), or this org keeps no record for them
 *    at all, so there is no record head for "this is you" to be read on. Which of
 *    those it was is not a fact about the file, so the reason collapses them.
 *
 * Either way the row is imported **in full** — this is never a failed row, and
 * `ImportCounts.links_skipped` counts it apart from `by_outcome.failed` for that
 * reason.
 */
export const ImportLinkSkip = z
  .object({
    user_id: Uuid.nullable(),
    reason: z.string().min(1),
  })
  .strict();
export type ImportLinkSkip = z.infer<typeof ImportLinkSkip>;

/**
 * One person the file produces, as the People tab lists them.
 *
 * `locator` is the parser's address for the record — the sheet row, the heading —
 * and is what every write in the session is keyed by, so re-uploading the same
 * bytes is a no-op at row level (§1.9). It is a string because a document's
 * locator is not a number.
 */
export const ImportRow = z
  .object({
    locator: z.string().min(1),
    /**
     * **The identity the row folds on** — the identifier the plan chose, or the
     * normalized name when the row carries none (§1.5). On the wire because a
     * `folded` row's whole explanation is this value: "the same identifier
     * arrived twice" is not a thing the People tab can say without naming which
     * one.
     *
     * Required, and neither nullable nor optional, because there is no moment at
     * which a row has not got one: the key is the sha256 of the row's canonical
     * text and is computed as the row is parsed, before any preview, which is
     * also why the column beneath it is `NOT NULL`.
     */
    record_key: z.string().min(1),
    /**
     * The row's own foreign id when the plan named one, in the shape the anchor
     * stores it — `{namespace: 'monday:puntabrava', id: '4821'}`. Two halves
     * rather than one string for the reason the anchor value has two: the
     * namespace is ours and the id is theirs, and it is stored as those two
     * halves — a `jsonb` column, not a flattened string — so "this id arrived
     * twice in one session" is an index and not a parse.
     */
    external_id: ImportExternalId.nullable(),
    display_name: z.string(),
    level: ResolutionLevel,
    /** Null on a `known` or `set_aside` row — only a subject has a status. */
    status: SubjectStatus.nullable(),
    /** Null until the session has been previewed. */
    preview: ImportRowPreview.nullable(),
    resolution: ImportRowResolution.nullable(),
    outcome: ImportRowOutcome,
    /** The person this row became; null until the commit reaches it. */
    entity_id: Uuid.nullable(),
    error: z.string().nullable(),
    /**
     * Set only where the commit wrote the person and **not** the membership link
     * it previewed — see {@link ImportLinkSkip}. `null` on every other row, which
     * is all but a handful of them.
     */
    link_skipped: ImportLinkSkip.nullable(),
    /**
     * **The copies of one name, bracketed** (§1.5) — the survivor's locator on
     * every row of a duplicate group, and `null` on every row that is not in
     * one.
     *
     * The list route returns a group's rows adjacent and in the file's order, so
     * the tab draws one card per group; this is what says where a card begins
     * and ends, without the client re-deriving a grouping the server has already
     * made. It is the first entry of {@link ImportRowPreview.duplicate_group},
     * named once here so that reading it is not a convention every client keeps
     * by hand.
     */
    group_key: z.string().min(1).nullable(),
    /**
     * **How far the reading has got on this row's own notes** (§1.11) — the
     * prose units this session filed on the person this row became, counted by
     * what the extractor has made of each.
     *
     * `null` **before the commit**, where there is nothing to report: the units
     * do not exist yet, and what the row *will* file is
     * {@link ImportRowPreview.notes} instead. Null too on a committed row that
     * became nobody — a skipped row, a row that failed.
     *
     * The reading is a leg of its own behind the people (§1.9), so a row lands
     * long before what its bio says does, and this is what lets the People tab
     * say so per person rather than only as a session-wide "412 of 1,540".
     *
     * `total` is every unit filed, `read` the ones the extractor came back on,
     * `failed` the ones it would not read, `pending` the ones still queued. The
     * three do not have to sum to the total: a unit the plan keeps as background
     * is filed and never queued (§1.12), so it is in `total` and in none of the
     * rest.
     *
     * Counted off the **person**, not off the row, because that is where prose
     * lives: two rows of one file that folded onto one person report that
     * person's units, which is the same answer to the same question asked twice.
     */
    notes_status: z
      .object({
        total: z.number().int().nonnegative(),
        read: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        pending: z.number().int().nonnegative(),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type ImportRow = z.infer<typeof ImportRow>;

/* ————————————————————————— requests ————————————————————————— */

/**
 * **The description is mandatory and it is the whole point** (§1.1, §1.2): the
 * model sees the description and the profile and nothing else, and a good
 * description gets zero questions and a plan. Eight characters is the floor
 * because "contacts" is a description and "x" is a placeholder.
 */
export const IMPORT_DESCRIPTION_MIN_LENGTH = 8;

export const IMPORT_DESCRIPTION_TOO_SHORT =
  'say what this file is in a line — "the club contact board, all groups", "Lew\'s LinkedIn ' +
  'connections" — it is what the plan is proposed from';

/**
 * The cap, for the same reason the org profile has one: this string lands in a
 * model's user turn, and an unbounded one is not inert. A one-line description
 * has nothing to say past this.
 */
export const IMPORT_DESCRIPTION_MAX_LENGTH = 500;

export const IMPORT_DESCRIPTION_TOO_LONG =
  `a description is one line — ${IMPORT_DESCRIPTION_MAX_LENGTH} characters — and the questions ` +
  'the session asks are where the rest of it goes';

/**
 * `POST /imports`, which is **multipart**: the file rides beside this body rather
 * than in it (Express's default 100 kB JSON limit is why, §5). What is validated
 * here is the description.
 */
export const CreateImportBody = z
  .object({
    description: z
      .string()
      .trim()
      .min(IMPORT_DESCRIPTION_MIN_LENGTH, IMPORT_DESCRIPTION_TOO_SHORT)
      .max(IMPORT_DESCRIPTION_MAX_LENGTH, IMPORT_DESCRIPTION_TOO_LONG),
    /**
     * **Import into this event's attendee graph** rather than into the record
     * (event graph design §3). A tracked event of this org; the session's
     * target is fixed at create.
     */
    event_id: Uuid.optional(),
    /**
     * **Replace the event's attendee list** with this file rather than adding
     * to it (docs/notes/2026-09-30-replace-attendee-list-spec.md). A multipart
     * field, so "true" or "false"; only with `event_id`.
     */
    replace_list: z.stringbool().optional(),
  })
  .strict()
  .refine((body) => !body.replace_list || body.event_id !== undefined, {
    path: ['replace_list'],
    message: 'Only an attendee list can replace one.',
  });
export type CreateImportBody = z.infer<typeof CreateImportBody>;

/**
 * **The longest thing the office may say in one turn.**
 *
 * Every message is carried into the model's context on every later turn of the
 * session, so an unbounded field is not one long message — it is a transcript
 * that grows without a ceiling and a per-turn bill that grows with it. Four
 * thousand characters is several paragraphs, which is more than anybody has
 * needed to say about a spreadsheet, and it is a limit the office meets with a
 * refusal on the way in rather than with a surprising invoice.
 */
export const IMPORT_MESSAGE_MAX_LENGTH = 4_000;

/**
 * `POST /imports/:id/messages` — one stateless turn over the transcript.
 *
 * `answers` names the standing questions this message settles, so the next turn
 * can put each question in front of the model beside its answer instead of
 * hoping a sentence on the transcript reads as one. The office answers a
 * message's questions as a batch and sends them once, so `text` may be empty
 * when `answers` is not: a sentence nobody typed would reach the model as
 * something the office had just said. A body with neither is refused.
 *
 * A question counts as answered once a user message later than the agent
 * message that asked it carries an answer for its id, and the server refuses
 * a second answer to it with `409 IMPORT_QUESTION_ANSWERED` — a double press
 * must not cost two turns. `changes` lists the ids the office is deliberately
 * answering again; for those the later answer replaces the earlier one.
 *
 * `retry` is the one repeat that is nobody's second thought: after
 * `502 IMPORT_PLANNER_UNAVAILABLE` the office's own row is already written,
 * answers and all, so the retry resends the failed body and this says that is
 * what it is. With `retry` set the server takes each answer only where it is
 * exactly what the record already holds as the newest answer for that id, and
 * refuses with `409 IMPORT_QUESTION_ANSWERED` any id whose newest answer on the
 * session is now something else — a later message changed it, and repeating a
 * reading of the record must never overwrite somebody's newer one — or that the
 * session has not settled at all. `changes` is not read on a retry: a retry
 * says the record again, and `changes` is for saying something different.
 */
export const SendImportMessageBody = z
  .object({
    text: z.string().trim().max(IMPORT_MESSAGE_MAX_LENGTH),
    answers: z.array(ImportAnswer).optional(),
    changes: z.array(z.string().min(1)).optional(),
    retry: z.boolean().optional(),
  })
  .strict()
  .refine((body) => body.text.length > 0 || (body.answers?.length ?? 0) > 0, {
    message: 'A message says something or answers something',
  });
export type SendImportMessageBody = z.infer<typeof SendImportMessageBody>;

/**
 * **What both planner routes answer** — `POST /imports/:id/open` and `POST
 * /imports/:id/messages` alike, because they differ only in how many turns they
 * wrote.
 *
 * `messages` is what this call put on the transcript, oldest first: one on an
 * opening turn, two on a message (the office's own, then the answer). A second
 * `open` writes nothing and answers the standing first turn, which is the same
 * shape and is why the route is a 200.
 *
 * `plan` is the version this turn proposed, or the head of the chain on that
 * idempotent second open, and `null` on a turn that only asked or only answered.
 * `phase` is the sentence the ledger shows beside the session (§2.4).
 */
export const ImportTurnResponse = z
  .object({
    messages: z.array(ImportMessage),
    plan: ImportPlanVersion.nullable(),
    phase: ImportPhase.nullable(),
  })
  .strict();
export type ImportTurnResponse = z.infer<typeof ImportTurnResponse>;

/**
 * `POST /imports/:id/rows/:locator` — the per-row gesture (§1.8): choose a level,
 * set aside, fix the name inline, pick a namesake. Every key is optional and an
 * absent one is left as it stands.
 */
export const SetRowBody = z
  .object({
    level: ResolutionLevel.optional(),
    status: SubjectStatus.optional(),
    name: z.string().trim().min(1).optional(),
    /** The candidate entity for an `ambiguous` row. */
    pick: Uuid.optional(),
    /** Named relationship target -> selected row in this same import. */
    relation_picks: z.record(z.string().min(1), z.string().min(1)).optional(),
    /**
     * **"Same person" or "different people"** on a `duplicate_name` row or an
     * `identifier_conflict` one — see {@link DuplicateAnswer} and
     * {@link ImportRowConflict}.
     *
     * The answer is about the *group* and the door writes it on every row of it,
     * including the one this call names — the repeats of one name, or the rows
     * of one disagreement, whichever the row is being asked about. Sent on a row
     * no rule caught it is simply an answer to a question nobody asked, and is
     * stored as one.
     */
    duplicate: DuplicateAnswer.optional(),
  })
  .strict();
export type SetRowBody = z.infer<typeof SetRowBody>;

/**
 * Which rows a bulk edit reaches — the same filter the rows read takes
 * (`?bin=|outcome=|q=`), plus an explicit list for "these ones I ticked".
 * Everything absent is everything.
 */
export const ImportRowFilter = z
  .object({
    /** Only rows in the needs-a-look bin, or only rows out of it. */
    bin: z.boolean().optional(),
    /**
     * **Only the rows one bin rule caught** — the chip beside the bin count,
     * whose numbers are {@link ImportCounts.by_bin_reason}. It is the filter a
     * bulk gesture actually wants: "set aside every row that is an address" is
     * one rule, and `bin: true` alone would sweep the namesakes in with them.
     * Reads the row's *stored* reason, which is the dry run's, so a row the
     * office has since answered still carries the rule it was caught by.
     */
    bin_reason: BinRule.optional(),
    /**
     * **Only the rows the dry run said one thing about** — the counts are
     * {@link ImportCounts.by_preview_outcome}.
     *
     * Beside `outcome` rather than instead of it, because the two are different
     * questions with the same five-ish words: `outcome` is what the commit did
     * and is `pending` on every row until it has run, and this is what the dry
     * run said it *would* do. "Show me the 25 that will attach" is a question
     * asked before the commit, and it is the one a bulk gesture is usually
     * reaching for.
     */
    preview_outcome: ImportPreviewOutcome.optional(),
    outcome: ImportRowOutcome.optional(),
    level: ResolutionLevel.optional(),
    status: SubjectStatus.optional(),
    q: z.string().trim().min(1).optional(),
    locators: z.array(z.string().min(1)).min(1).optional(),
  })
  .strict();
export type ImportRowFilter = z.infer<typeof ImportRowFilter>;

/**
 * What a bulk edit sets. Deliberately narrower than {@link SetRowBody}: a name
 * and a namesake pick are answers about **one person**, and setting either across
 * a filter would be a way to get one person's answer onto forty rows.
 */
export const BulkRowsSet = z
  .object({
    level: ResolutionLevel.optional(),
    status: SubjectStatus.optional(),
    /**
     * **The duplicate answer, over every same-name group the filter reaches** —
     * the bulk half of {@link DuplicateAnswer}, and the one bulk gesture that is
     * not simply "this value on every matched row".
     *
     * A duplicate is a question about a *pair*, so the answer is applied per
     * group: `same` folds each group's later rows onto its own earliest row, and
     * a group that has any row in the filter is answered whole, so the gesture
     * never leaves half a pair holding a question. An `identifier_conflict` row's
     * group is {@link ImportRowPreview.conflict}'s rows, answered the same way
     * and with the same care.
     *
     * It is here where a name and a pick are not, because it is not one person's
     * answer written onto forty rows: "these repeats are repeats" is a statement
     * about the file, which is exactly what the office is looking at when the
     * `duplicate_name` chip is the filter.
     */
    duplicate: DuplicateAnswer.optional(),
  })
  .strict();
export type BulkRowsSet = z.infer<typeof BulkRowsSet>;

/** `POST /imports/:id/rows/bulk`. */
export const BulkRowsBody = z.object({ filter: ImportRowFilter, set: BulkRowsSet }).strict();
export type BulkRowsBody = z.infer<typeof BulkRowsBody>;

/**
 * `POST /imports/:id/commit`.
 *
 * `read_notes` is required and has no default here on purpose: the Import tab's
 * switch defaults **on** and says beside it what reading does, so what arrives
 * is always a choice the office looked at. A default in the contract would let
 * a client commit without having made it.
 *
 * There is no per-session spend ceiling (run defaults sweep, 2026-09-26): the
 * organization's monthly limit is what stops the reading, and the body is
 * `.strict()`, so a stale client's `spend_ceiling_usd` is refused.
 *
 * `confirm_findings` is what the office has read on this page (§4.3), **each as
 * its kind and the sentence they were shown** — see {@link ConfirmedFinding} for
 * why the sentence travels rather than the kind alone. The confirmation rides on
 * the press rather than living in a door or a column of its own for the reason
 * `read_notes` does: it is a decision about *this* commit, and it is recorded
 * where that decision is — the `import_commit` decision carries
 * `confirmed_findings`, so the trail says what was confirmed and not only that
 * something was. The server recomputes the findings from the records it is about
 * to write and refuses with `IMPORT_HALTED` where one of them has no entry here
 * carrying the same kind and the identical sentence.
 */
export const CommitImportBody = z
  .object({
    read_notes: z.boolean(),
    /** Absent is the same as none — a request body, where optional is allowed. */
    confirm_findings: z.array(ConfirmedFinding).optional(),
    /**
     * The credit price the Import button showed, in ledger units (an account
     * enrolled in live charging; see `ImportCreditPrice`). A higher price is
     * refused with `CREDIT_PRICE_CHANGED`. Absent accepts only a free commit.
     */
    expected_credit_units: z.number().int().min(0).optional(),
    /**
     * A replace only: attendees brought into the records whom the new file
     * drops, and whom the office takes off the list. Absent is none: they stay.
     */
    drop_from_event: z.array(Uuid).max(1000).optional(),
  })
  .strict();
export type CommitImportBody = z.infer<typeof CommitImportBody>;

/**
 * `POST /imports/:id/notes/read` — **the notes the office decided against, and
 * then decided for** (§1.11, §1.12).
 *
 * The commit's `read_notes` switch is a choice made once, and "no" was until now
 * permanent: the units were filed `none` and nothing could
 * ever move them. But the file is exactly the sort of thing an office reads the
 * people out of first and the notes out of later — Lew's own posture on the
 * bios PDF — and a second import of the same bytes would file every note again
 * rather than read the ones already there.
 *
 * `all` is which notes: `false`, the default, is the ones **the plan marked to
 * extract**, which is the same set the original switch would have queued.
 * `true` is every note this session filed,
 * which is the office overruling the plan — the earlier tool's dossiers included
 * (§1.12), and the reason it is a deliberate flag rather than the default.
 *
 * This door is also what a session **paused at the organization's monthly
 * limit** is pressed with once the allowance moves: units already pending are
 * queued again. In a spent month it is refused with
 * `ORG_MONTHLY_SPEND_CEILING_REACHED`.
 *
 * Answers with the session, so the caller reads back the queue this just made —
 * the counts and what it has spent so far.
 */
export const ReadImportNotesBody = z
  .object({
    all: z.boolean().default(false),
  })
  .strict();
export type ReadImportNotesBody = z.infer<typeof ReadImportNotesBody>;

/**
 * `POST /imports/:id/rows/retry` — **the rows that failed for a reason somebody
 * has since put right** (§1.8).
 *
 * A row fails for a reason of its own and its neighbours land: a status word the
 * vertical does not support, a fact whose shape the catalog refused. Each of
 * those is fixable — and until this door there was nothing to press afterwards,
 * because the session was committed and a `failed` row carries a receipt saying
 * it is finished. This clears that receipt and lets the phase reach them again;
 * the plan, the dry run and the office's answers are all untouched, which is what
 * makes it a retry rather than a second import.
 */
export const RetryImportRowsResponse = z
  .object({ retried: z.number().int().nonnegative() })
  .strict();
export type RetryImportRowsResponse = z.infer<typeof RetryImportRowsResponse>;

/**
 * `GET /imports/:id/rows` — the People tab, paged. The filter is
 * {@link ImportRowFilter}'s, minus the explicit locator list, which is a bulk
 * edit's way of saying "these ones I ticked" and not a thing to page through.
 */
export const ListImportRowsQuery = z
  .object({
    /** `true` is only the needs-a-look bin; `false` is only what is out of it. */
    bin: z.enum(['true', 'false']).optional(),
    /** One bin rule's rows — the chip, whose count is `counts.by_bin_reason`. */
    bin_reason: BinRule.optional(),
    /** What the dry run said — see {@link ImportRowFilter.preview_outcome}. */
    preview_outcome: ImportPreviewOutcome.optional(),
    outcome: ImportRowOutcome.optional(),
    level: ResolutionLevel.optional(),
    status: SubjectStatus.optional(),
    q: z.string().trim().min(1).optional(),
    /**
     * **Keep the copies of one name together** (§1.5) — the ordering the People
     * tab reads the repeats in, and the owner's own complaint about the first
     * one: a row saying "same person" while the other copy is forty rows further
     * down is a question nobody can answer.
     *
     * A grouping, never a filter: it selects nothing and excludes nothing, it
     * only moves each row of a duplicate group up beside the earliest copy of its
     * name, groups in the order those earliest copies appear. Implied by
     * `bin_reason=duplicate_name`, because a list *of* the repeats that scattered
     * them would be the same complaint again.
     */
    group: z.enum(['duplicates']).optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
  })
  .strict();
export type ListImportRowsQuery = z.infer<typeof ListImportRowsQuery>;
export type ListImportRowsQueryInput = z.input<typeof ListImportRowsQuery>;

/**
 * Keyset, on the locator — the one column every row has and no two share. Under
 * `group=duplicates` the key is the group's place followed by the locator, which
 * is still one value per row and still ordered; the client hands back whatever it
 * was given either way.
 */
export const ImportRowsResponse = z
  .object({
    rows: z.array(ImportRow),
    /** Where to resume, or null at the end of the list. */
    cursor: z.string().nullable(),
    /** Rows the filter matched, whatever this page holds. */
    total: z.number().int().nonnegative(),
  })
  .strict();
export type ImportRowsResponse = z.infer<typeof ImportRowsResponse>;

/** What a bulk edit changed. */
export const BulkRowsResponse = z.object({ updated: z.number().int().nonnegative() }).strict();
export type BulkRowsResponse = z.infer<typeof BulkRowsResponse>;
